import { useState, useEffect, useRef, useCallback, Suspense, lazy } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import type { NetworkManager } from "./lib/network";
import {
  GameState,
  GameStatus,
  NetworkMessage,
  Card as CardType,
  Player,
  PenaltyType,
  Suit,
  GameType,
  GameSpeed,
} from "./types";
import {
  INITIAL_GAME_STATE,
  DeckManager,
  PistiEngine,
  BatakEngine,
  KingEngine,
  OkeyEngine,
} from "./lib/engine";
import { soundManager } from "./lib/sound";
import confetti from "canvas-confetti";
import { AIService } from "./services/aiService";
import { resolveAvatarUrl, AVATAR_MAP, safeRequestFullscreen, setupFullscreenRecoveryOnInteraction } from "./lib/utils";
import { initGA, trackPageView, trackEvent, setUserId, hasAnalyticsConsent } from "./lib/analytics";

import { Mic, MicOff, Globe, AlertTriangle } from "lucide-react";
import { CookieBanner } from "./components/CookieBanner";
import ReloadPrompt from "./components/ReloadPrompt";
import { PageLoader } from "./components/PageLoader";

import { useVoiceChat } from "./hooks/useVoiceChat";
import { useGameStore, useUserStore } from "./hooks/useGameState";
import { useWakeLock } from "./hooks/useWakeLock";

import { EmojiBurstOverlay } from "./components/EmojiBurstOverlay";
import { READY_EMOJIS } from "./components/ReactionPanel";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LandingPage } from "./components/LandingPage";

// Code splitting with dynamic imports for heavy game table & policy components
const Table = lazy(() => import("./components/Table"));
const OkeyTable = lazy(() => import("./components/OkeyTable"));
const CookiePolicy = lazy(() => import("./components/CookiePolicy").then(m => ({ default: m.CookiePolicy })));
const TermsOfUse = lazy(() => import("./components/TermsOfUse").then(m => ({ default: m.TermsOfUse })));

const TEXTURES_LIST = [
  "https://www.transparenttextures.com/patterns/felt.png",
  "https://www.transparenttextures.com/patterns/carbon-fibre.png",
  "https://www.transparenttextures.com/patterns/black-linen-2.png",
  "https://www.transparenttextures.com/patterns/cross-stripes.png",
  "https://www.transparenttextures.com/patterns/wood-pattern.png",
  "https://www.transparenttextures.com/patterns/arabesque.png",
  "https://www.transparenttextures.com/patterns/cubes.png",
  "https://www.transparenttextures.com/patterns/luxury.png",
  "https://www.transparenttextures.com/patterns/old-wall.png",
  "https://www.transparenttextures.com/patterns/purty-wood.png",
  "https://www.transparenttextures.com/patterns/diagmonds-light.png",
  "https://www.transparenttextures.com/patterns/micro-carbon.png",
];

function loadImage(url: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = url;
    img.onload = () => resolve();
    img.onerror = () => resolve();
  });
}

function loadAudio(url: string): Promise<void> {
  return new Promise((resolve) => {
    const audio = new Audio();
    audio.src = url;
    audio.preload = "auto";
    audio.addEventListener("canplaythrough", () => resolve(), { once: true });
    audio.addEventListener("error", () => resolve(), { once: true });
    setTimeout(resolve, 1500);
  });
}

const GAME_SEO_METAS: Record<string, { title: string; desc: string; canonical: string }> = {
  pisti: {
    title: "Online Pişti Oyna - Canlı, Ücretsiz & Eşli Pişti Masası | pisti.online",
    desc: "Gerçek oyuncularla veya akıllı yapay zekaya karşı ücretsiz online pişti oyna. İster tekli ister eşli, hızlı ve canlı pişti deneyimi pisti.online'da.",
    canonical: "https://pisti.online/pisti"
  },
  batak: {
    title: "Online Batak Oyna - İhaleli, Eşli & Koz Maça Batak | pisti.online",
    desc: "Ücretsiz online Batak oyna! İhaleli Batak, eşli batak, gömmeli ve koz maça seçenekleriyle canlı odalarda veya bota karşı hemen oyna.",
    canonical: "https://pisti.online/batak"
  },
  king: {
    title: "Online King Oyna - Rıfkı, Ceza & Koz İskambil Oyunu | pisti.online",
    desc: "Klasik King iskambil oyununu online, canlı ve ücretsiz oyna. Rıfkı, kız almaz, son iki ve kupa almaz cezalarıyla ustalık gerektiren King masaları seni bekliyor.",
    canonical: "https://pisti.online/king"
  },
  okey: {
    title: "Online Okey Oyna - Klasik 4 Kişilik Canlı Okey Masası | pisti.online",
    desc: "Arkadaşlarınla veya gerçek oyuncularla canlı online okey oyna. Seri taş dizme, gösterge ve çifte gitme heyecanıyla tarayıcıdan anında bedava okey keyfi.",
    canonical: "https://pisti.online/okey"
  }
};

const DEFAULT_SEO = {
  title: "Pişti, Batak, King ve Okey Online - Ücretsiz Çok Oyunculu Kart & Masa Oyunları | pisti.online",
  desc: "Arkadaşlarınla veya akıllı botlara karşı ücretsiz Pişti, İhaleli Batak, Koz Maça, King ve Düz Okey oyna. İndirmeden, sesli sohbet ve canlı odalarla hemen katıl!",
  canonical: "https://pisti.online/"
};

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const getInitialGameType = (): GameType => {
    const params = new URLSearchParams(location.search);
    const gameParam = params.get('game')?.toLowerCase();
    if (gameParam === 'batak') return 'batak';
    if (gameParam === 'king') return 'king';
    if (gameParam === 'okey') return 'okey';
    if (gameParam === 'pisti') return 'pisti';

    const path = location.pathname.toLowerCase().replace('/', '');
    if (path === 'batak') return 'batak';
    if (path === 'king') return 'king';
    if (path === 'okey') return 'okey';
    return 'pisti';
  };

  const [gameState, setGameState] = useState<GameState | null>(null);
  const gameStateRef = useRef<GameState | null>(null);
  gameStateRef.current = gameState;

  const isSamePlayerId = (a?: string, b?: string): boolean => {
    if (!a || !b) return false;
    if (a === b) return true;
    const aTrim = a.trim();
    const bTrim = b.trim();
    if (aTrim === bTrim) return true;
    if (aTrim.length >= 6 && bTrim.length >= 6) {
      return aTrim.includes(bTrim) || bTrim.includes(aTrim);
    }
    return false;
  };
  const buildCleanSearch = useCallback((searchStr: string): string => {
    const params = new URLSearchParams(searchStr);
    params.delete('game');
    params.delete('source');
    const str = params.toString();
    return str ? `?${str}` : '';
  }, []);

  const [gameType, setGameType] = useState<GameType>(getInitialGameType());
  const [inGame, setInGame] = useState(false);
  const [roomCodeFromUrl, setRoomCodeFromUrl] = useState<string | null>(() => {
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search);
      const code = p.get('room') || p.get('join');
      return code ? code.trim().toUpperCase() : null;
    }
    return null;
  });
  const [hasInitialGameShortcut] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search);
      const g = p.get('game')?.toLowerCase();
      return !!g && ['batak', 'king', 'okey', 'pisti'].includes(g);
    }
    return false;
  });
  const [showLobbyOnLanding, setShowLobbyOnLanding] = useState(!!roomCodeFromUrl || hasInitialGameShortcut);
  const [lobbyInitialStep, setLobbyInitialStep] = useState<'TYPE' | 'MODE' | 'JOIN'>(roomCodeFromUrl ? 'JOIN' : 'MODE');

  const handleGameTypeChange = useCallback((rawType: GameType | string) => {
    const lower = String(rawType || 'pisti').toLowerCase();
    const validType: GameType =
      lower === 'batak' || lower === 'king' || lower === 'okey' || lower === 'pisti'
        ? (lower as GameType)
        : 'pisti';
    setGameType(validType);
    if (!inGame) {
      const cleanSearch = buildCleanSearch(location.search);
      navigate(`/${validType}${cleanSearch}`, { replace: true });
    }
  }, [inGame, location.search, navigate, buildCleanSearch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const gameParam = params.get('game')?.toLowerCase();
    const hasSourceParam = params.has('source');

    // If launched via a PWA game shortcut (?game=pisti|batak|king|okey), consume it immediately,
    // switch to that game (even if previously in another game), and strip ?game= from the URL
    // so subsequent game switches inside the app are never locked.
    if (gameParam && ['batak', 'king', 'okey', 'pisti'].includes(gameParam)) {
      const targetGame = gameParam as GameType;
      setGameType(targetGame);
      if (inGame) {
        setInGame(false);
        setGameState(null);
      }
      setShowLobbyOnLanding(true);
      setLobbyInitialStep('MODE');
      const cleanSearch = buildCleanSearch(location.search);
      navigate(`/${targetGame}${cleanSearch}`, { replace: true });
      return;
    }

    if (!inGame) {
      const path = location.pathname.toLowerCase().replace(/^\/+|\/+$/g, '');
      if (path === 'batak' || path === 'king' || path === 'okey' || path === 'pisti') {
        setGameType(path as GameType);
        if (hasSourceParam) {
          const cleanSearch = buildCleanSearch(location.search);
          navigate(`/${path}${cleanSearch}`, { replace: true });
        }
      } else {
        const cleanSearch = buildCleanSearch(location.search);
        navigate(`/${gameType}${cleanSearch}`, { replace: true });
      }
    }
  }, [location.pathname, location.search, inGame, navigate, buildCleanSearch]);

  // Support PWA Launch Handler API (launchQueue) when tapping home screen shortcuts while PWA is already open
  useEffect(() => {
    const w = window as any;
    if (w.launchQueue && typeof w.launchQueue.setConsumer === 'function') {
      w.launchQueue.setConsumer((launchParams: any) => {
        if (!launchParams?.targetURL) return;
        try {
          const url = new URL(launchParams.targetURL, window.location.origin);
          const gParam = url.searchParams.get('game')?.toLowerCase();
          const pName = url.pathname.toLowerCase().replace(/^\/+|\/+$/g, '');
          const resolved: GameType | null =
            gParam && ['batak', 'king', 'okey', 'pisti'].includes(gParam)
              ? (gParam as GameType)
              : ['batak', 'king', 'okey', 'pisti'].includes(pName)
                ? (pName as GameType)
                : null;
          if (resolved) {
            setGameType(resolved);
            setInGame(false);
            setGameState(null);
            setShowLobbyOnLanding(true);
            setLobbyInitialStep('MODE');
            const cleanSearch = buildCleanSearch(url.search);
            navigate(`/${resolved}${cleanSearch}`, { replace: true });
          }
        } catch {
          // Ignore malformed launch URL
        }
      });
    }
  }, [navigate, buildCleanSearch]);

  const [preloadProgress] = useState(100);
  const [preloadText] = useState("");
  const [preloadComplete] = useState(true);
  const [myId, setMyId] = useState<string>(() => "P" + Math.random().toString(36).substring(2, 7).toUpperCase());
  const [shortId, setShortId] = useState<string>("");


  // Analytics hooks: initialize GA first, then set pseudonymous User-ID if KVKK consent was granted
  useEffect(() => {
    initGA();
    if (!hasAnalyticsConsent()) return;
    let persistentId = "";
    try {
      persistentId = localStorage.getItem("persistent_player_id") || "";
      if (!persistentId) {
        persistentId = "usr_" + Math.random().toString(36).substring(2, 10);
        localStorage.setItem("persistent_player_id", persistentId);
      }
    } catch {
      // Ignore
    }
    if (persistentId) {
      setUserId(persistentId);
    }
  }, []);

  useEffect(() => {
    if (gameState?.status === 'finished') {
      trackEvent("game_over", gameState.gameType || "unknown", gameState.gameMode || "unknown");
    }
  }, [gameState?.status, gameState?.gameType, gameState?.gameMode]);


  const [isDisconnected, setIsDisconnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  
  const { isMicMuted, setIsMicMuted, isVoiceActive, setIsVoiceActive, toggleMute, handlePeerAudioStream } = useVoiceChat();
  useWakeLock(inGame);
  
  const [chatMessages, setChatMessages] = useState<Record<string, string>>({});
  const [selectedSkinId, setSelectedSkinId] = useState<number>(() => {
    try {
      const saved = localStorage.getItem("selectedSkinId");
      return saved !== null ? parseInt(saved, 10) : 0;
    } catch { return 0; }
  });
  const [gameSpeed, setGameSpeed] = useState<GameSpeed>(() => {
    try {
      const saved = localStorage.getItem("game_speed");
      if (saved === "normal" || saved === "fast") {
        return saved as GameSpeed;
      }
    } catch { /* ignore */ }
    return "normal";
  });

  useEffect(() => {
    const handleSpeedChange = (e: any) => {
      const speed = e.detail || localStorage.getItem("game_speed");
      if (speed === "normal" || speed === "fast") {
        setGameSpeed(speed as GameSpeed);
      }
    };
    window.addEventListener("gameSpeedChange", handleSpeedChange);
    return () => window.removeEventListener("gameSpeedChange", handleSpeedChange);
  }, []);
  const [selectedTheme, setSelectedTheme] = useState<string>(() => {
    try { return localStorage.getItem("selectedTheme") || "casino"; } catch { return "casino"; }
  });
  const [colorTheme, setColorTheme] = useState<string>(() => {
    try { return localStorage.getItem("colorTheme") || "theme-cyan"; } catch { return "theme-cyan"; }
  });
  const [showCookiePolicy, setShowCookiePolicy] = useState(false);
  const [showTermsOfUse, setShowTermsOfUse] = useState(false);
  const [faceStyle, setFaceStyle] = useState<
    | "original"
    | "classic"
    | "relief"
    | "elegant"
    | "minimalist"
    | "royal_gold"
    | "dark_knight"
    | "vintage"
    | "neon"
    | "casino"
    | "holographic"
    | "wooden"
    | "ocean"
    | "obsidian"
    | "original_vintage"
    | "pixel"
    | "cyberpunk"
    | "glassmorphism"
  >(() => {
    try { return (localStorage.getItem("faceStyle") as any) || "original"; } catch { return "original"; }
  });
  const [userName, setUserName] = useState<string>(() => {
    try { return localStorage.getItem("userName") || "Oyuncu 1"; } catch { return "Oyuncu 1"; }
  });
  const [userAvatar, setUserAvatar] = useState<string>(() => {
    try {
      const saved = localStorage.getItem("userAvatar");
      if (saved) return resolveAvatarUrl(saved);
    } catch {}
    return AVATAR_MAP['ahmet'];
  });
  const [stats, setStats] = useState<{
    gamesPlayed: number;
    gamesWon: number;
    totalScore: number;
  }>(() => {
    try {
      const saved = localStorage.getItem("playerStats");
      return saved
        ? JSON.parse(saved)
        : { gamesPlayed: 0, gamesWon: 0, totalScore: 0 };
    } catch {
      return { gamesPlayed: 0, gamesWon: 0, totalScore: 0 };
    }
  });
  const networkRef = useRef<NetworkManager | null>(null);
  const stateSeqRef = useRef<number>(0);
  const lastTurnInfoRef = useRef({ turnIndex: -1, status: '', deckLength: 0 });

  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [showNetworkNotice, setShowNetworkNotice] = useState<boolean>(false);

  useEffect(() => {
    let timer: any;
    const handleOnline = () => {
      setIsOnline(true);
      setShowNetworkNotice(true);
      timer = setTimeout(() => setShowNetworkNotice(false), 4500);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowNetworkNotice(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    if (typeof navigator !== "undefined" && !navigator.onLine) {
      setIsOnline(false);
      setShowNetworkNotice(true);
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Synchronize document title, meta tags, and canonical URL structure during SPA transitions
  useEffect(() => {
    let path = location.pathname;
    let title = DEFAULT_SEO.title;
    let desc = DEFAULT_SEO.desc;
    let canonical = DEFAULT_SEO.canonical;

    if (inGame && gameState) {
      const type = gameState.gameType || "pisti";
      const capitalized = type.charAt(0).toUpperCase() + type.slice(1);
      path = `/game/${type}`;
      title = `${capitalized} Masası - Pisti.online`;
      desc = `${capitalized} oyun masası. Pisti.online üzerinde gerçek oyuncularla ve akıllı botlarla canlı oyun.`;
      canonical = GAME_SEO_METAS[type]?.canonical || `https://pisti.online/${type}`;
    } else {
      const routeType = path.replace(/^\/+|\/+$/g, '').toLowerCase();
      const effectiveRoute = GAME_SEO_METAS[routeType] ? routeType : gameType;
      path = `/${effectiveRoute}`;
      if (GAME_SEO_METAS[effectiveRoute]) {
        title = GAME_SEO_METAS[effectiveRoute].title;
        desc = GAME_SEO_METAS[effectiveRoute].desc;
        canonical = GAME_SEO_METAS[effectiveRoute].canonical;
      }
    }

    // Enforce fixed document title
    document.title = title;

    // Enforce fixed canonical URL link
    const canEl = document.getElementById("canonical-url");
    if (canEl) canEl.setAttribute("href", canonical);

    // Sync Open Graph & Twitter meta tags
    const descMeta = document.querySelector('meta[name="description"]');
    if (descMeta) descMeta.setAttribute("content", desc);

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", title);

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", desc);

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", canonical);

    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute("content", title);

    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc) twDesc.setAttribute("content", desc);

    const twUrl = document.querySelector('meta[name="twitter:url"]');
    if (twUrl) twUrl.setAttribute("content", canonical);

    trackPageView(path, title);
  }, [location.pathname, inGame, gameState?.gameType, gameType]);

  useEffect(() => {
    try { localStorage.setItem("selectedSkinId", selectedSkinId.toString()); } catch {}
  }, [selectedSkinId]);

  useEffect(() => {
    try { localStorage.setItem("selectedTheme", selectedTheme); } catch {}
  }, [selectedTheme]);

  useEffect(() => {
    try { localStorage.setItem("colorTheme", colorTheme); } catch {}
  }, [colorTheme]);

  useEffect(() => {
    try { localStorage.setItem("faceStyle", faceStyle); } catch {}
  }, [faceStyle]);

  useEffect(() => {
    try { localStorage.setItem("userName", userName); } catch {}
  }, [userName]);

  useEffect(() => {
    try { localStorage.setItem("userAvatar", userAvatar); } catch {}
  }, [userAvatar]);

  useEffect(() => {
    try { localStorage.setItem("playerStats", JSON.stringify(stats)); } catch {}
  }, [stats]);

  useEffect(() => {
    // We removed the turn start sound
  }, [gameState?.turnIndex, gameState?.status, gameState?.deck?.length, inGame, myId]);

  useEffect(() => {
    if (gameState?.status === "finished") {
      const myPlayerInfo = gameState.players.find((p) => p.id === myId);
      const maxScore = Math.max(...gameState.players.map((p) => p.score));
      let isWinner = false;

      if (myPlayerInfo) {
        if (gameState.isTeamGame) {
          const myTeam = myPlayerInfo.team;
          const teamMaxScores = gameState.players
            .filter((p) => p.team === myTeam)
            .map((p) => p.score);
          const teamMax = Math.max(...teamMaxScores);
          isWinner = teamMax === maxScore; // Or both teams have same score if tie? Yes
        } else {
          isWinner = myPlayerInfo.score === maxScore;
        }

        setStats((prev) => ({
          gamesPlayed: prev.gamesPlayed + 1,
          gamesWon: isWinner ? prev.gamesWon + 1 : prev.gamesWon,
          totalScore: prev.totalScore + myPlayerInfo.score,
        }));

        try {
          const lb = JSON.parse(localStorage.getItem("leaderboard") || "[]");
          lb.push({
            id: Date.now().toString(),
            date: new Date().toISOString(),
            gameType: gameState.gameType,
            score: myPlayerInfo.score,
            won: isWinner,
          });
          localStorage.setItem("leaderboard", JSON.stringify(lb));
        } catch {}
      }
    }
  }, [gameState?.status]);

  const handleRemoteMessageRef = useRef<(msg: NetworkMessage) => void>(
    () => {},
  );
  const handlePeerDisconnectRef = useRef<(id: string) => void>(() => {});

  const isAmIHost = (state: GameState | null | undefined, currentId: string): boolean => {
    if (!state || !state.players || state.players.length === 0) return true;
    if (!currentId) return true;
    if ((state as any).hostId) {
      const hid = (state as any).hostId;
      return isSamePlayerId(hid, currentId);
    }
    const hostPlayer = state.players.find((p) => p.isHost);
    if (hostPlayer) {
      return isSamePlayerId(hostPlayer.id, currentId);
    }
    const firstHuman = state.players.find((p) => !p.isBot);
    if (firstHuman) {
      return isSamePlayerId(firstHuman.id, currentId);
    }
    const humanCount = state.players.filter((p) => !p.isBot).length;
    if (humanCount <= 1) return true;
    return false;
  };

  useEffect(() => {
    handleRemoteMessageRef.current = handleRemoteMessage;
    handlePeerDisconnectRef.current = handlePeerDisconnect;
  });

  const networkInitPromiseRef = useRef<Promise<NetworkManager> | null>(null);

  const setupNetwork = useCallback((forceNew = false): Promise<NetworkManager> => {
    if (!forceNew && networkRef.current) {
      return Promise.resolve(networkRef.current);
    }
    if (!forceNew && networkInitPromiseRef.current) {
      return networkInitPromiseRef.current;
    }

    const promise = (async () => {
      const { NetworkManager: DynamicNetworkManager } = await import("./lib/network");
      if (networkRef.current) {
        networkRef.current.disconnect();
      }
      const nw = new DynamicNetworkManager();
      networkRef.current = nw;

      const applyNetworkId = (id: string, sId?: string) => {
        setMyId((prevId) => {
          if (prevId && prevId !== id) {
            setGameState((prevState) => {
              if (!prevState) return prevState;
              const updatedPlayers = prevState.players.map((p) => {
                if (p.id === prevId || (!p.isBot && p.isHost && prevState.players[0]?.id === prevId)) {
                  return { ...p, id };
                }
                return p;
              });
              const updated = { ...prevState, players: updatedPlayers };
              if (isAmIHost(prevState, prevId)) {
                networkRef.current?.broadcast("SYNC_STATE", updated);
              } else {
                networkRef.current?.broadcast("UPDATE_PLAYER_ID", { oldId: prevId, newId: id });
              }
              return updated;
            });
          }
          return id;
        });
        if (sId) setShortId(sId);
      };

      applyNetworkId(nw.myId, nw.shortId);
      nw.onIdChange(applyNetworkId);

      nw.onConnection(() => {
        // Connection established
      });

      nw.onMessage((msg: NetworkMessage) => {
        handleRemoteMessageRef.current(msg);
      });

      nw.onDisconnect((id: string) => {
        const el = document.getElementById(`audio-${id}`);
        if (el && el.parentNode) {
          el.parentNode.removeChild(el);
        }
        handlePeerDisconnectRef.current(id);
      });

      nw.onAudioStream((peerId: string, stream: MediaStream) => {
        handlePeerAudioStream(peerId, stream);
      });

      return nw;
    })();

    networkInitPromiseRef.current = promise;
    return promise;
  }, []);

  useEffect(() => {
    if (roomCodeFromUrl || showLobbyOnLanding || inGame) {
      setupNetwork();
    }
  }, [roomCodeFromUrl, showLobbyOnLanding, inGame, setupNetwork]);

  useEffect(() => {
    const onFirstUserAction = () => {
      setupNetwork();
      window.removeEventListener("pointerdown", onFirstUserAction, true);
      window.removeEventListener("touchstart", onFirstUserAction, true);
      window.removeEventListener("keydown", onFirstUserAction, true);
    };

    window.addEventListener("pointerdown", onFirstUserAction, { capture: true, passive: true, once: true });
    window.addEventListener("touchstart", onFirstUserAction, { capture: true, passive: true, once: true });
    window.addEventListener("keydown", onFirstUserAction, { capture: true, passive: true, once: true });

    const checkInterval = setInterval(() => {
      if (networkRef.current) {
        setIsDisconnected(networkRef.current.isDisconnected());
      }
    }, 2000);

    return () => {
      window.removeEventListener("pointerdown", onFirstUserAction, true);
      window.removeEventListener("touchstart", onFirstUserAction, true);
      window.removeEventListener("keydown", onFirstUserAction, true);
      if (networkRef.current) {
        networkRef.current.disconnect();
      }
      clearInterval(checkInterval);
    };
  }, [setupNetwork]);

  useEffect(() => {
    if (inGame) {
      safeRequestFullscreen();
    }
  }, [inGame]);

  const sanitizeStateForPlayer = (fullState: GameState, targetPlayerId: string): GameState => {
    if (!fullState) return fullState;
    // Showdown or game over: all hands can be inspected
    if (fullState.status === "finished") {
      return fullState;
    }

    // Mask opponents closed hands and racks so no client can inspect them in memory or network
    const sanitizedPlayers = fullState.players.map((p) => {
      const isTarget = isSamePlayerId(p.id, targetPlayerId);
      if (isTarget) {
        // Player sees their own actual hand and rack
        return p;
      }
      // Opponents cards: mask suit and value, keeping only closed card back placeholder with exact count
      const maskedHand = (p.hand || []).map((c) => ({
        id: c.id,
        suit: "spades" as const,
        rank: "2" as const,
        value: 0,
        isFaceUp: false,
      }));

      let maskedOkeyState = undefined;
      if (p.okeyState) {
        maskedOkeyState = {
          ...p.okeyState,
          rack: (p.okeyState.rack || []).map((t) => ({
            id: t.id,
            color: "black" as const,
            number: 0,
            value: "",
            isFakeOkey: false,
          })),
        };
      }

      return {
        ...p,
        hand: maskedHand,
        okeyState: maskedOkeyState,
      };
    });

    // Mask remaining upcoming cards in the deck
    const sanitizedDeck = (fullState.deck || []).map((c) => ({
      id: c.id,
      suit: "spades" as const,
      rank: "2" as const,
      value: 0,
      isFaceUp: false,
    }));

    // Mask remaining tiles in the okey tile bag
    const sanitizedOkeyDeck = (fullState.okeyDeck || []).map((t) => ({
      id: t.id,
      color: "black" as const,
      value: 0,
      isFake: false,
    }));

    return {
      ...fullState,
      players: sanitizedPlayers,
      deck: sanitizedDeck,
      okeyDeck: sanitizedOkeyDeck,
    };
  };

  const broadcastState = (state: GameState | null) => {
    if (!state || !networkRef.current) return;
    const humanPeers = state.players.filter((p) => !p.isBot && p.id !== myId);
    if (humanPeers.length === 0) return;

    stateSeqRef.current += 1;
    const sequencedState: GameState = {
      ...state,
      stateSeq: stateSeqRef.current,
      serverTimestamp: Date.now(),
    };

    if (state.status === "waiting" || state.status === "finished") {
      networkRef.current.broadcast("SYNC_STATE", sequencedState);
      return;
    }

    for (const peer of humanPeers) {
      const sanitized = sanitizeStateForPlayer(sequencedState, peer.id);
      networkRef.current.sendTo(peer.id, "SYNC_STATE", sanitized);
    }
  };

  const handlePeerDisconnect = (id: string) => {
    if (!id || !id.trim()) return;
    setGameState((prev) => {
      if (!prev) return prev;

      const disconnectedPlayer = prev.players.find(
        (p) => !p.isBot && isSamePlayerId(p.id, id)
      );
      if (!disconnectedPlayer) return prev;

      // Check if disconnected player is the Host
      if (disconnectedPlayer.isHost) {
        console.warn(
          "Oda yöneticisi oyundan ayrıldı. Host devri ve bot geçişi yapılıyor.",
        );

        // Find remaining human players
        const remainingHumans = prev.players.filter(
          (p) => !p.isBot && !isSamePlayerId(p.id, disconnectedPlayer.id)
        );

        // The first remaining human in player order becomes the new host
        const newHostPlayer = remainingHumans[0];
        const amINewHost = newHostPlayer && isSamePlayerId(newHostPlayer.id, myId);

        let newPlayers = prev.players.map((p) => {
          if (isSamePlayerId(p.id, disconnectedPlayer.id)) {
            return {
              ...p,
              isBot: true,
              isHost: false,
              originalPlayerId: p.originalPlayerId || p.id,
              originalName: p.originalName || p.name.replace(/\s*\(Bot\)$/i, ""),
              name: p.name.includes("(Bot)") ? p.name : p.name + " (Bot)",
            };
          }
          if (newHostPlayer && isSamePlayerId(p.id, newHostPlayer.id)) {
            return { ...p, isHost: true };
          }
          return { ...p, isHost: false };
        });

        // For 4-player games (Okey, Batak, King), preserve exact circular seat positions (0, 1, 2, 3)
        // so table rotation, discard bowls, and turn indices remain 100% stable.
        let newTurnIndex = prev.turnIndex;
        let newBidderIndex = prev.currentBidderIndex;
        let newFirstIndex = prev.firstPlayerIndex;
        let newDealerIndex = prev.dealerIndex;

        // Only in 2-player Pişti when transitioning to solo vs bot:
        if (prev.gameType === "pisti" && remainingHumans.length <= 1) {
          const myIndex = newPlayers.findIndex((p) => isSamePlayerId(p.id, myId));
          if (myIndex > 0) {
            const temp = newPlayers[0];
            newPlayers[0] = newPlayers[myIndex];
            newPlayers[myIndex] = temp;

            const swapIndex = (oldIndex?: number) => {
              if (oldIndex === undefined) return oldIndex;
              if (oldIndex === 0) return myIndex;
              if (oldIndex === myIndex) return 0;
              return oldIndex;
            };
            newTurnIndex = swapIndex(newTurnIndex) as number;
            newBidderIndex = swapIndex(newBidderIndex);
            newFirstIndex = swapIndex(newFirstIndex);
            newDealerIndex = swapIndex(newDealerIndex);
          }
        }

        let newState: GameState = {
          ...prev,
          players: newPlayers,
          turnIndex: newTurnIndex,
          currentBidderIndex: newBidderIndex,
          firstPlayerIndex: newFirstIndex,
          dealerIndex: newDealerIndex,
        };

        if (newState.gameType === "okey") {
          newState = OkeyEngine.replenishBotRacks(newState);
        }

        if (amINewHost) {
          // As new host, stay connected to other human peers and broadcast state
          broadcastState(newState);
          if (remainingHumans.length <= 1) {
            // Solo against bots, disconnect network cleanly
            networkRef.current?.disconnect();
          }
        } else if (remainingHumans.length === 0) {
          // Solo against bots
          networkRef.current?.disconnect();
        }

        return newState;
      }

      // Disconnected player is a guest (non-host):
      const myPlayer = prev.players.find((p) => isSamePlayerId(p.id, myId));
      const newPlayers = prev.players.map((p) => {
        if (isSamePlayerId(p.id, disconnectedPlayer.id)) {
          return {
            ...p,
            isBot: true,
            isHost: false,
            originalPlayerId: p.originalPlayerId || p.id,
            originalName: p.originalName || p.name.replace(/\s*\(Bot\)$/i, ""),
            name: p.name.includes("(Bot)") ? p.name : p.name + " (Bot)",
          };
        }
        return p;
      });

      let newState: GameState = { ...prev, players: newPlayers };

      if (newState.gameType === "okey") {
        newState = OkeyEngine.replenishBotRacks(newState);
      }

      if (myPlayer?.isHost) {
        broadcastState(newState);
      }

      return newState;
    });
  };

  const handleRemoteMessage = (msg: NetworkMessage) => {
    switch (msg.type) {
      case "PLAYER_LEAVING":
        if (msg.payload?.id) {
          handlePeerDisconnect(msg.payload.id);
        } else if (msg.senderId) {
          handlePeerDisconnect(msg.senderId);
        }
        break;
      case "SYNC_STATE": {
        const incomingSeq = msg.payload?.stateSeq;
        const currentSeq = gameStateRef.current?.stateSeq;
        if (
          typeof incomingSeq === "number" &&
          typeof currentSeq === "number" &&
          incomingSeq < currentSeq
        ) {
          // Ignore older out-of-order state
          break;
        }

        let syncedState = msg.payload;
        if (syncedState && syncedState.gameType === "okey" && Array.isArray(syncedState.players)) {
          const currentLocal = gameStateRef.current || gameState;
          const myLocalPlayer = currentLocal?.players.find((p) => isSamePlayerId(p.id, myId));
          const myLocalRack = myLocalPlayer?.okeyState?.rack;
          const isLocalRackValid =
            Array.isArray(myLocalRack) &&
            myLocalRack.length >= 14 &&
            !myLocalRack.some((t) => !t || (t.color === "black" && ((t.value as any) === "" || t.value === 0)));

          if (isLocalRackValid) {
            const myIncomingIdx = syncedState.players.findIndex((p: any) => isSamePlayerId(p.id, myId));
            if (myIncomingIdx !== -1) {
              const incomingRack = syncedState.players[myIncomingIdx].okeyState?.rack;
              const isIncomingDummyOrEmpty =
                !Array.isArray(incomingRack) ||
                incomingRack.length === 0 ||
                incomingRack.some((t: any) => !t || (t.color === "black" && ((t.value as any) === "" || t.value === 0)));

              if (isIncomingDummyOrEmpty) {
                // Preserve my valid rack!
                const updatedPlayers = [...syncedState.players];
                updatedPlayers[myIncomingIdx] = {
                  ...updatedPlayers[myIncomingIdx],
                  okeyState: {
                    ...updatedPlayers[myIncomingIdx].okeyState,
                    rack: myLocalRack,
                    discards: updatedPlayers[myIncomingIdx].okeyState?.discards || myLocalPlayer?.okeyState?.discards || [],
                    score: updatedPlayers[myIncomingIdx].okeyState?.score ?? myLocalPlayer?.okeyState?.score ?? 0,
                  },
                };
                syncedState = { ...syncedState, players: updatedPlayers };

                // Report real rack to host so host possesses accurate state
                networkRef.current?.sendTo(msg.senderId, "UPDATE_PLAYER_RACK", {
                  playerId: myId,
                  rack: myLocalRack,
                });
              }
            }
          }
        }

        setGameState(syncedState);
        setInGame(true);
        setIsConnecting(false);
        break;
      }
      case "REJECT_JOIN":
        setIsConnecting(false);
        setInGame(false);
        setConnectionError(msg.payload.reason || "Bağlantı reddedildi.");
        break;
      case "JOIN_LOBBY":
        if (networkRef.current) {
          const activeState = gameStateRef.current || gameState;
          const currentPlayers = activeState?.players || [];
          const isTeam = activeState?.isTeamGame ?? false;
          const gType = activeState?.gameType ?? gameType;
          const maxPlayers = activeState?.maxPlayers || (gType === "pisti" && !isTeam ? 2 : 4);

          // Idempotency: If this player is already registered with this senderId, simply resend state
          const alreadyJoined = currentPlayers.find((p) => p.id === msg.senderId);
          if (alreadyJoined && activeState) {
            const stateToSend =
              activeState.status === "waiting" || activeState.status === "finished"
                ? activeState
                : sanitizeStateForPlayer(activeState, msg.senderId);
            networkRef.current.sendTo(msg.senderId, "SYNC_STATE", stateToSend);
            return;
          }

          let pName = msg.payload.name || `Oyuncu ${currentPlayers.length + 1}`;
          const cleanPName = pName.trim().toLowerCase();

          // Check if this is a reconnecting player who dropped and was substituted by a bot
          const rejoiningBotIndex = currentPlayers.findIndex(
            (p) => p.isBot && (
              (p.originalPlayerId && p.originalPlayerId === msg.senderId) ||
              (p.originalName && p.originalName.toLowerCase() === cleanPName) ||
              p.name.toLowerCase().includes(cleanPName)
            )
          );

          if (activeState !== null && activeState.status !== "waiting") {
            // In active game, only allow rejoining if replacing a substituted bot
            if (rejoiningBotIndex === -1) {
              console.warn("Room is already playing and no disconnected bot slot matches");
              networkRef.current.sendTo(msg.senderId, "REJECT_JOIN", { reason: "Oyun zaten başladı." });
              return;
            }
          }
          
          const humanCount = currentPlayers.filter((p) => !p.isBot).length;
          if (humanCount >= maxPlayers && rejoiningBotIndex === -1) {
            console.warn("Room is full of human players");
            networkRef.current.sendTo(msg.senderId, "REJECT_JOIN", { reason: "Oda dolu." });
            return;
          }

          // Check if name is taken by another active human player
          const isNameTakenByHuman = currentPlayers.some(
            (p) => !p.isBot && p.id !== msg.senderId && p.name.trim().toLowerCase() === cleanPName
          );
          if (isNameTakenByHuman) {
            pName = `Oyuncu ${currentPlayers.length + 1}`;
          }

          const newPlayer: Player = {
            id: msg.senderId,
            name: pName,
            avatar: msg.payload.avatar || pName.toLowerCase(),
            isHost: false,
            isBot: false,
            hand: [],
            score: 0,
            collectedCards: [],
            pistiCount: 0,
            remainingPenalties: [
              "tricks",
              "hearts",
              "girls",
              "boys",
              "king-hearts",
              "last-two",
            ],
            remainingTrumps: 2,
            bid: 0,
            team: isTeam
              ? (["A", "B"] as const)[currentPlayers.length % 2]
              : undefined,
          };

          let updatedPlayers: Player[];
          // Prioritize rejoining bot match, then name match, then any bot if in lobby
          let botIndex = rejoiningBotIndex;
          if (botIndex === -1 && activeState?.status === "waiting") {
            botIndex = currentPlayers.findIndex(
              (p) => p.isBot && p.name.toLowerCase().includes(cleanPName)
            );
            if (botIndex === -1) {
              botIndex = currentPlayers.findIndex((p) => p.isBot);
            }
          }

          if (botIndex !== -1) {
            updatedPlayers = [...currentPlayers];
            const replacedBot = updatedPlayers[botIndex];
            if (isTeam && !newPlayer.team) {
              newPlayer.team = replacedBot.team || ((["A", "B"] as const)[botIndex % 2]);
            }
            // Seamlessly retain any hand/score/okey state if rejoining mid-game or in lobby
            if (replacedBot.hand?.length) newPlayer.hand = replacedBot.hand;
            if (replacedBot.score) newPlayer.score = replacedBot.score;
            if (replacedBot.collectedCards?.length) newPlayer.collectedCards = replacedBot.collectedCards;
            if (replacedBot.pistiCount) newPlayer.pistiCount = replacedBot.pistiCount;
            if (replacedBot.remainingPenalties?.length) newPlayer.remainingPenalties = replacedBot.remainingPenalties;
            if (replacedBot.remainingTrumps !== undefined) newPlayer.remainingTrumps = replacedBot.remainingTrumps;
            if (replacedBot.bid !== undefined) newPlayer.bid = replacedBot.bid;
            if (replacedBot.okeyState) newPlayer.okeyState = replacedBot.okeyState;

            updatedPlayers[botIndex] = newPlayer;
          } else {
            updatedPlayers = [...currentPlayers, newPlayer];
          }

          const newState: GameState = activeState
            ? { ...activeState, players: updatedPlayers }
            : {
                ...INITIAL_GAME_STATE(myId, gameType, userName, userAvatar),
                players: updatedPlayers,
              };

          updateGameState(newState);
          setInGame(true);
        }
        break;
      case "UPDATE_PLAYER_ID":
        if (gameState && isAmIHost(gameState, myId)) {
          const { oldId, newId } = msg.payload || {};
          if (newId) {
            setGameState((prev) => {
              if (!prev) return prev;
              const updatedPlayers = prev.players.map((p) => {
                if (p.id === oldId || p.id === msg.senderId) {
                  return { ...p, id: newId };
                }
                return p;
              });
              const newState = { ...prev, players: updatedPlayers };
              broadcastState(newState);
              return newState;
            });
          }
        }
        break;
      case "PLAYER_ACTION": {
        const activeState = gameStateRef.current || gameState;
        if (activeState && isAmIHost(activeState, myId)) {
          const senderCardId = msg.payload?.cardId;
          const senderIsBluff = msg.payload?.isBluff;
          const turnPlayer = activeState.players[activeState.turnIndex];
          const isSenderTurn = turnPlayer && (
            isSamePlayerId(turnPlayer.id, msg.senderId) ||
            isSamePlayerId(turnPlayer.id, msg.payload?.playerId) ||
            (msg.payload?.playerName && turnPlayer.name && turnPlayer.name.trim().toLowerCase() === msg.payload.playerName.trim().toLowerCase()) ||
            (!turnPlayer.isBot && activeState.players.filter((p) => !p.isBot).length <= 2 && !isSamePlayerId(msg.senderId, myId))
          );

          if (activeState.status !== "discarding" && !isSenderTurn) {
            console.warn(
              `[Security] Hile Girişimi Engellendi! Sırada olmayan oyuncu kart atmaya çalıştı: ${msg.senderId}`,
            );
            broadcastState(activeState);
            return;
          }
          const targetPlayerId = turnPlayer ? turnPlayer.id : msg.senderId;
          handlePlayCard(senderCardId, targetPlayerId, senderIsBluff, true);

          if (msg.payload?.stateHash) {
            DeckManager.computeHash(JSON.stringify(activeState)).then(
              (localHash) => {
                if (localHash !== msg.payload.stateHash) {
                  console.warn(
                    `[Network] Minor state hash discrepancy detected, but authority remains with the host. Handled gracefully.`,
                  );
                }
              },
            );
          }
        }
        break;
      }
      case "BID_ACTION": {
        const activeState = gameStateRef.current || gameState;
        if (activeState && isAmIHost(activeState, myId)) {
          handleBidGame(msg.payload.bid);
        }
        break;
      }
      case "RESOLVE_BLUFF": {
        const activeState = gameStateRef.current || gameState;
        if (activeState && isAmIHost(activeState, myId)) {
          const matchedPlayer = activeState.players.find((p) => isSamePlayerId(p.id, msg.senderId));
          handleResolveBluff(matchedPlayer ? matchedPlayer.id : msg.senderId, msg.payload.isChallenge);
        }
        break;
      }
      case "SELECT_GAME": {
        const activeState = gameStateRef.current || gameState;
        if (activeState && isAmIHost(activeState, myId)) {
          handleSelectGame(msg.payload.type, msg.payload.bid);
        }
        break;
      }
      case "OKEY_DISCARD_ACTION": {
        const activeState = gameStateRef.current || gameState;
        if (activeState && isAmIHost(activeState, myId)) {
          const matchedPlayer = activeState.players.find((p) => isSamePlayerId(p.id, msg.senderId));
          handleOkeyDiscard(matchedPlayer ? matchedPlayer.id : msg.senderId, msg.payload.tileId);
        }
        break;
      }
      case "OKEY_DRAW_ACTION": {
        const activeState = gameStateRef.current || gameState;
        if (activeState && isAmIHost(activeState, myId)) {
          const matchedPlayer = activeState.players.find((p) => isSamePlayerId(p.id, msg.senderId));
          handleOkeyDraw(matchedPlayer ? matchedPlayer.id : msg.senderId, msg.payload.source);
        }
        break;
      }
      case "OKEY_TOGGLE_DOUBLE": {
        const activeState = gameStateRef.current || gameState;
        if (activeState && isAmIHost(activeState, myId)) {
          const matchedPlayer = activeState.players.find((p) => isSamePlayerId(p.id, msg.senderId));
          handleOkeyToggleDouble(matchedPlayer ? matchedPlayer.id : msg.senderId);
        }
        break;
      }
      case "OKEY_SHOW_INDICATOR": {
        const activeState = gameStateRef.current || gameState;
        if (activeState && isAmIHost(activeState, myId)) {
          const matchedPlayer = activeState.players.find((p) => isSamePlayerId(p.id, msg.senderId));
          handleOkeyShowIndicator(matchedPlayer ? matchedPlayer.id : msg.senderId);
        }
        break;
      }
      case "UPDATE_PLAYER_RACK": {
        const activeState = gameStateRef.current || gameState;
        if (activeState && isAmIHost(activeState, myId)) {
          const { playerId, rack } = msg.payload || {};
          if (playerId && Array.isArray(rack) && rack.length >= 14) {
            setGameState((prev) => {
              if (!prev || prev.gameType !== "okey") return prev;
              const pIdx = prev.players.findIndex((p) => isSamePlayerId(p.id, playerId));
              if (pIdx === -1) return prev;
              const newPlayers = [...prev.players];
              newPlayers[pIdx] = {
                ...newPlayers[pIdx],
                okeyState: {
                  ...newPlayers[pIdx].okeyState,
                  rack,
                  discards: newPlayers[pIdx].okeyState?.discards || [],
                  score: newPlayers[pIdx].okeyState?.score || 0,
                },
              };
              const newState = { ...prev, players: newPlayers };
              broadcastState(newState);
              return newState;
            });
          }
        }
        break;
      }
      case "CHAT_MESSAGE":
        {
          const effectiveSender = msg.payload?.senderId || msg.senderId;
          if (effectiveSender !== myId) {
            handleChatMessage(effectiveSender, msg.payload.text);
            // If host receives message, relay to all other peers so every guest sees it
            const isHost = isAmIHost(gameState, myId);
            if (isHost && networkRef.current) {
              networkRef.current.broadcast("CHAT_MESSAGE", {
                text: msg.payload.text,
                senderId: effectiveSender,
              });
            }
          }
        }
        break;
      case "UPDATE_PLAYER":
        if (gameState && isAmIHost(gameState, myId) && msg.senderId !== myId) {
          handleUpdatePlayer(msg.senderId, msg.payload);
        }
        break;
      case "COMMIT_SEED":
        if (commitRevealStateRef.current.completed) {
          commitRevealStateRef.current = {
            hashes: {},
            seeds: {},
            expectedPeers: [],
            completed: false,
          };
        }

        if (!commitRevealStateRef.current.mySeed) {
          const mySeed = Math.floor(Math.random() * 0xffffffff);
          commitRevealStateRef.current.mySeed = mySeed;
          if (gameState) {
            commitRevealStateRef.current.expectedPeers = gameState.players
              .filter((p) => !p.isBot && p.id !== myId)
              .map((p) => p.id);
          } else {
            commitRevealStateRef.current.expectedPeers = [msg.senderId];
          }
          commitRevealStateRef.current.hashes[msg.senderId] = msg.payload.hash;
          DeckManager.computeHash(mySeed.toString()).then((myHash) => {
            commitRevealStateRef.current.hashes[myId] = myHash;
            networkRef.current?.broadcast("COMMIT_SEED", { hash: myHash });
            checkCommitPhase();
          });
        } else {
          commitRevealStateRef.current.hashes[msg.senderId] = msg.payload.hash;
          if (gameState) {
            commitRevealStateRef.current.expectedPeers = gameState.players
              .filter((p) => !p.isBot && p.id !== myId)
              .map((p) => p.id);
          }
          checkCommitPhase();
        }
        break;
      case "REVEAL_SEED":
        commitRevealStateRef.current.seeds[msg.senderId] = msg.payload.seed;
        checkRevealPhase();
        break;
    }
  };

  const handleUpdatePlayer = (playerId: string, updates: Partial<Player>) => {
    setGameState((prev) => {
      if (!prev) return null;
      const index = prev.players.findIndex((p) => p.id === playerId);
      if (index === -1) return prev;

      const newPlayers = [...prev.players];
      newPlayers[index] = { ...newPlayers[index], ...updates };

      const newState = { ...prev, players: newPlayers };
      if (isAmIHost(prev, myId))
        broadcastState(newState);
      return newState;
    });
  };

  const handleAddBot = () => {
    setGameState((prev) => {
      if (!prev || !prev.players[0].isHost) return prev;
      const maxPlayers =
        prev.maxPlayers ||
        (prev.gameType === "pisti" && !prev.isTeamGame ? 2 : 4);
      if (prev.players.length >= maxPlayers) return prev;

      const PREDEFINED_AVATAR_KEYS = ['ahmet', 'alp', 'asli', 'can', 'ece', 'elif', 'emre', 'gizem', 'mehmet', 'ozan', 'selin', 'zeynep'];
      const BOT_NAMES = [
        "Zeynep",
        "Elif",
        "Ece",
        "Asli",
        "Gizem",
        "Selin",
        "Ahmet",
        "Mehmet",
        "Can",
        "Ozan",
        "Emre",
        "Alp",
      ];
      const existingNames = new Set(prev.players.map((p) => p.name.toLowerCase()));
      const existingAvatars = new Set(
        prev.players.map((p) => resolveAvatarUrl(p.avatar || p.name))
      );

      const unusedBotNames = BOT_NAMES.filter((name) => !existingNames.has(name.toLowerCase()));
      const finalNamesList = unusedBotNames.length > 0 ? unusedBotNames : BOT_NAMES;

      const unusedAvatars = PREDEFINED_AVATAR_KEYS.filter(
        (key) => !existingAvatars.has(resolveAvatarUrl(key))
      );
      const finalAvatarPool = unusedAvatars.length > 0 ? unusedAvatars : PREDEFINED_AVATAR_KEYS;

      const perfectMatches = finalNamesList.filter((name) =>
        finalAvatarPool.includes(name.toLowerCase())
      );

      let chosenName: string;
      let chosenAvatarKey: string;

      if (perfectMatches.length > 0) {
        chosenName = perfectMatches[Math.floor(Math.random() * perfectMatches.length)];
        chosenAvatarKey = chosenName.toLowerCase();
      } else {
        chosenName = finalNamesList[Math.floor(Math.random() * finalNamesList.length)];
        chosenAvatarKey = finalAvatarPool[Math.floor(Math.random() * finalAvatarPool.length)];
      }

      const botId = `bot-${Math.random().toString(36).substr(2, 5)}`;
      const newBot: Player = {
        id: botId,
        name: chosenName,
        avatar: chosenAvatarKey,
        isHost: false,
        isBot: true,
        hand: [],
        score: 0,
        collectedCards: [],
        pistiCount: 0,
        remainingPenalties: [
          "tricks",
          "hearts",
          "girls",
          "boys",
          "king-hearts",
          "last-two",
        ],
        remainingTrumps: 2,
        bid: 0,
        team: prev.isTeamGame
          ? (["A", "B"] as const)[prev.players.length % 2]
          : undefined,
      };

      const newState = { ...prev, players: [...prev.players, newBot] };
      broadcastState(newState);
      return newState;
    });
  };

  const handleRemovePlayer = (id: string) => {
    if (gameState && gameState.status !== "waiting") {
      // In active game, do not truncate player array! Replace cleanly with a bot
      handlePeerDisconnect(id);
      return;
    }
    setGameState((prev) => {
      if (!prev || !prev.players[0].isHost) return prev;
      if (prev.status !== "waiting") {
        return prev;
      }
      const newState = {
        ...prev,
        players: prev.players.filter((p) => p.id !== id),
      };
      broadcastState(newState);
      return newState;
    });
  };

  const handleToggleTeam = (id: string) => {
    setGameState((prev) => {
      if (!prev) return prev;
      const index = prev.players.findIndex((p) => p.id === id);
      if (index === -1) return prev;

      const newPlayers = [...prev.players];
      newPlayers[index].team = newPlayers[index].team === "A" ? "B" : "A";

      const newState = { ...prev, players: newPlayers };
      if (isAmIHost(prev, myId))
        broadcastState(newState);
      return newState;
    });
  };

  const handleResolveBluff = (accepterId: string, isChallenge: boolean) => {
    if (
      !gameState ||
      !gameState.activeBluff ||
      gameState.status !== "bluff_challenge"
    )
      return;

    setGameState((prev) => {
      if (!prev || !prev.activeBluff) return prev;

      const { card, pile } = prev.activeBluff;
      const isCapture = PistiEngine.canCapture(card, pile.slice(0, -1));

      const nextMiddlePile = [...pile];
      if (nextMiddlePile.length > 0) {
        nextMiddlePile[nextMiddlePile.length - 1] = { ...card, isFaceUp: true };
      }

      const blufferId = prev.activeBluff.playerId;
      const challengerId = accepterId;
      let winnerId = "";
      let wasBluffLying = false;

      if (!isChallenge) {
        winnerId = blufferId;
      } else {
        if (isCapture) {
          winnerId = blufferId;
        } else {
          winnerId = challengerId;
          wasBluffLying = true;
        }
      }

      const newState: GameState = {
        ...prev,
        status: "bluff_reveal",
        middlePile: nextMiddlePile,
        bluffResult: {
          winnerId,
          challengerId,
          blufferId,
          isChallenge,
          wasBluffLying,
        },
      };

      if (isAmIHost(prev, myId)) {
        broadcastState(newState);
      }

      setTimeout(() => {
        handleEndBluffReveal(isChallenge, accepterId);
      }, 1500);

      return newState;
    });
  };

  const handleEndBluffReveal = (isChallenge: boolean, accepterId: string) => {
    setGameState((prev) => {
      if (!prev || !prev.activeBluff || prev.status !== "bluff_reveal")
        return prev;
      const hasMultipleHumans = prev.players.filter((p) => !p.isBot).length > 1;
      if (prev.networkMode === "P2P" && hasMultipleHumans && !isAmIHost(prev, myId)) {
        return prev;
      }

      let updatedPlayers = prev.players.map(p => ({ ...p, hand: [...p.hand], collectedCards: [...p.collectedCards], remainingPenalties: [...p.remainingPenalties] }));
      const blufferIdx = prev.players.findIndex(
        (p) => isSamePlayerId(p.id, prev.activeBluff!.playerId),
      );
      const challengerIdx = prev.players.findIndex((p) => isSamePlayerId(p.id, accepterId));

      const { card, pile } = prev.activeBluff;
      let nextTurnIndex = prev.turnIndex;
      let lastWinnerId = prev.lastWinnerId;
      let nextMiddlePile: any[] = [];
      let lastAction: GameState["lastAction"] = prev.lastAction;

      const isCapture = PistiEngine.canCapture(card, pile.slice(0, -1));
      const isMatchingPisti = PistiEngine.checkPisti(card, pile.slice(0, -1));
      const pistiValue = card.rank === "J" ? 2 : 1;
      const hadHidden =
        (prev.hasInitialHiddenCards ??
          prev.players.every((p) => (p.collectedCards?.length || 0) === 0)) &&
        pile.length >= 4;
      const initialHiddenCards = hadHidden
        ? pile.slice(0, 3).map((c) => ({ ...c, isFaceUp: true }))
        : undefined;

      if (!isChallenge) {
        updatedPlayers[blufferIdx].collectedCards.push(...pile);
        if (isMatchingPisti) {
          updatedPlayers[blufferIdx].pistiCount += pistiValue;
        }
        soundManager.play("collect");
        lastAction = {
          type: isMatchingPisti ? "pisti" : "capture",
          playerId: prev.activeBluff.playerId,
          card: { ...card, isFaceUp: true },
          capturedPile: [...pile.slice(0, -1), { ...card, isFaceUp: true }],
          timestamp: Date.now(),
          hadInitialHiddenCards: hadHidden,
          initialHiddenCards,
        };
        lastWinnerId = prev.activeBluff.playerId;
      } else {
        soundManager.play("cardPlay");
        if (isCapture) {
          updatedPlayers[blufferIdx].collectedCards.push(...pile);
          if (isMatchingPisti) {
            updatedPlayers[blufferIdx].pistiCount += pistiValue;
          }
          soundManager.play("collect");
          lastAction = {
            type: isMatchingPisti ? "pisti" : "capture",
            playerId: prev.activeBluff.playerId,
            card: { ...card, isFaceUp: true },
            capturedPile: [...pile.slice(0, -1), { ...card, isFaceUp: true }],
            timestamp: Date.now(),
            hadInitialHiddenCards: hadHidden,
            initialHiddenCards,
          };
          lastWinnerId = prev.activeBluff.playerId;
        } else {
          updatedPlayers[challengerIdx].collectedCards.push(...pile);
          if (pile.length === 2) {
            updatedPlayers[challengerIdx].pistiCount += pistiValue;
            soundManager.play("pisti");
          } else {
            soundManager.play("collect");
          }
          lastAction = {
            type: pile.length === 2 ? "pisti" : "capture",
            playerId: accepterId,
            card: { ...card, isFaceUp: true },
            capturedPile: [...pile.slice(0, -1), { ...card, isFaceUp: true }],
            timestamp: Date.now(),
            hadInitialHiddenCards: hadHidden,
            initialHiddenCards,
          };
          lastWinnerId = accepterId;
        }
      }

      const allEmpty = updatedPlayers.every((p) => p.hand.length === 0);
      let nextStatus = "playing";
      let nextDeck = prev.deck;

      if (allEmpty) {
        if (nextDeck.length === 0) {
          if (nextMiddlePile.length > 0) {
            const targetWinnerId =
              lastWinnerId ||
              updatedPlayers[prev.dealerIndex]?.id ||
              updatedPlayers[0]?.id;
            const winnerIndex = updatedPlayers.findIndex(
              (p) => p.id === targetWinnerId,
            );
            if (winnerIndex !== -1) {
              updatedPlayers[winnerIndex].collectedCards.push(
                ...nextMiddlePile,
              );
              nextMiddlePile = [];
            }
          }
          if (prev.isTeamGame) {
            const teamACards = updatedPlayers
              .filter((p) => p.team === "A")
              .flatMap((p) => p.collectedCards);
            const teamAPistis = updatedPlayers
              .filter((p) => p.team === "A")
              .reduce((acc, p) => acc + p.pistiCount, 0);
            const teamBCards = updatedPlayers
              .filter((p) => p.team === "B")
              .flatMap((p) => p.collectedCards);
            const teamBPistis = updatedPlayers
              .filter((p) => p.team === "B")
              .reduce((acc, p) => acc + p.pistiCount, 0);
            const isAMajority = teamACards.length > 26;
            const isBMajority = teamBCards.length > 26;
            const scoreA = PistiEngine.calculateScore(teamACards, teamAPistis, isAMajority);
            const scoreB = PistiEngine.calculateScore(teamBCards, teamBPistis, isBMajority);
            updatedPlayers = updatedPlayers.map((p) => ({
              ...p,
              score: p.score + (p.team === "A" ? scoreA : scoreB)
            }));
          } else {
            const maxCardCount = Math.max(
              ...updatedPlayers.map((p) => p.collectedCards.length),
            );
            const topCollectors = updatedPlayers.filter(
              (p) => p.collectedCards.length === maxCardCount,
            );
            const hasUniqueMajority =
              topCollectors.length === 1 && maxCardCount > 0;

            updatedPlayers = updatedPlayers.map((p) => {
              const isMajorityWinner =
                hasUniqueMajority && p.collectedCards.length === maxCardCount;
              return {
                ...p,
                score:
                  p.score +
                  PistiEngine.calculateScore(
                    p.collectedCards,
                    p.pistiCount,
                    isMajorityWinner,
                  ),
              };
            });
          }
          if (
            prev.targetScore &&
            updatedPlayers.some((p) => p.score >= prev.targetScore!)
          ) {
            nextStatus = "finished";
          } else if (!prev.targetScore) {
            nextStatus = "finished";
          }
        }
      }

      if (allEmpty && nextStatus === "finished") {
        const maxScore = Math.max(...updatedPlayers.map((p) => p.score));
        const myPlayer = updatedPlayers.find((p) => p.id === myId);
        if (myPlayer && myPlayer.score === maxScore) soundManager.play("win");
        else soundManager.play("lose");
      }

      if (allEmpty && nextStatus !== "finished") {
        if (nextDeck.length > 0) {
          const dealSize = Math.min(
            4,
            Math.floor(nextDeck.length / updatedPlayers.length),
          );
          updatedPlayers = updatedPlayers.map((p) => ({ ...p, hand: nextDeck.splice(0, dealSize) }));
          nextStatus = "playing";
        } else {
          // Setup Next Round for Pişti after a bluff round finishes the hands
          const newDeck = DeckManager.createDeck(selectedSkinId);
          updatedPlayers.forEach((p) => {
            p.collectedCards = [];
            p.pistiCount = 0;
            p.bid = 0;
          });
          const newDealerIndex = (prev.dealerIndex + 1) % prev.players.length;
          nextMiddlePile = newDeck.splice(0, 4);
          nextMiddlePile[3].isFaceUp = true;
          updatedPlayers = updatedPlayers.map((p) => ({ ...p, hand: newDeck.splice(0, 4) }));
          nextStatus = "playing";
          nextTurnIndex = (newDealerIndex + 1) % prev.players.length;
          nextDeck = newDeck;
        }
      }

      const newState: GameState = {
        ...prev,
        hasInitialHiddenCards:
          nextMiddlePile.length === 4 && nextMiddlePile[3]?.isFaceUp
            ? true
            : isCapture || !isChallenge
            ? false
            : prev.hasInitialHiddenCards,
        players: updatedPlayers,
        middlePile: nextMiddlePile,
        turnIndex: nextTurnIndex,
        lastWinnerId,
        lastAction,
        status: nextStatus as GameState["status"],
        deck: nextDeck,
        activeBluff: undefined,
      };

      if (isAmIHost(prev, myId))
        broadcastState(newState);
      return newState;
    });
  };

  const updateGameState = (newState: GameState) => {
    gameStateRef.current = newState;
    setGameState(newState);
    if (networkRef.current) {
      broadcastState(newState);
    }
  };

  const handleJoin = (hostId: string, name: string, avatar?: string) => {
    setShowLobbyOnLanding(false);
    setConnectionError(null);
    trackEvent("game_join", gameType, "multiplayer");
    safeRequestFullscreen();
    setIsConnecting(true);

    // Delay connection a bit to give the browser time to enter fullscreen securely
    setTimeout(async () => {
      try {
        await setupNetwork();
      } catch {}
      if (networkRef.current) {
        try {
          const actualHostId = await networkRef.current.connectTo(hostId);
          let joinAttempts = 0;
          const sendJoin = () => {
            if (networkRef.current) {
              networkRef.current.sendTo(actualHostId, "JOIN_LOBBY", { name, avatar });
            }
          };
          sendJoin();

          // Resend JOIN_LOBBY if SYNC_STATE has not arrived within 1.2s
          const retryInterval = setInterval(() => {
            joinAttempts++;
            if (gameStateRef.current && gameStateRef.current.status) {
              clearInterval(retryInterval);
              setIsConnecting(false);
              return;
            }
            if (joinAttempts >= 5) {
              clearInterval(retryInterval);
              return;
            }
            sendJoin();
          }, 1200);

          // 12s fallback timeout for lobby entry
          setTimeout(() => {
            clearInterval(retryInterval);
            if (!gameStateRef.current) {
              setIsConnecting(false);
              setConnectionError(
                localStorage.getItem("language") === "tr" || navigator.language.startsWith("tr")
                  ? "Oda yanıt vermedi. Lütfen oda kodunu kontrol edip tekrar deneyin."
                  : "Room response timed out. Please check the code and try again."
              );
            }
          }, 12000);
        } catch (err) {
          setIsConnecting(false);
          console.warn("Failed to connect to host:", err);
          // Clean up and re-prepare network so subsequent attempts aren't blocked by stale state
          try {
            networkRef.current?.disconnect();
          } catch {}
          setupNetwork(true);
          setConnectionError(
            localStorage.getItem("language") === "tr" || navigator.language.startsWith("tr")
              ? "Bağlantı kurulamadı. Oda bulunamadı veya bağlantı zaman aşımına uğradı. Lütfen oda kodunu kontrol edin."
              : "Could not connect. Room not found or connection timed out. Please check the room code."
          );
        }
      } else {
        setIsConnecting(false);
      }
    }, 500);
  };

  const handleHost = (
    name: string,
    type?: GameType,
    mode?: "AI" | "P2P",
    isTeamGame?: boolean,
    batakVariant?: "ihaleli" | "kozmaca" | "gomulu" | "esli",
    avatar?: string,
    maxPlayers?: number,
    targetScore?: number,
    bluffingMode?: boolean,
  ) => {
    setShowLobbyOnLanding(false);
    safeRequestFullscreen();
    if (mode !== "AI") {
      setupNetwork();
    }
    const initialState = INITIAL_GAME_STATE(
      myId,
      type || gameType,
      name,
      avatar,
      maxPlayers,
      targetScore,
      bluffingMode,
    );
    initialState.isTeamGame = !!isTeamGame;
    initialState.networkMode = mode || "P2P";
    if (batakVariant) initialState.batakVariant = batakVariant;
    setGameState(initialState);
    setInGame(true);

    if (mode === "AI") {
      trackEvent("game_start", type || gameType, "AI");
      handleStartGame(initialState);
    } else {
      trackEvent("game_host", type || gameType, "P2P");
    }
  };

  const handleRestartGame = () => {
    safeRequestFullscreen();
    if (!gameState) return;

    // Reset player scores and state to prepare for new match
    const resetPlayers = gameState.players.map((p) => ({
      ...p,
      hand: [],
      score: 0,
      collectedCards: [],
      pistiCount: 0,
      remainingPenalties: [
        "tricks",
        "hearts",
        "girls",
        "boys",
        "king-hearts",
        "last-two",
      ],
      remainingTrumps: 2,
      bid: 0,
    }));

    const resetState = {
      ...gameState,
      players: resetPlayers as any,
      dealerIndex: 0,
      turnIndex: 0,
      bidderIndex: 0,
      currentTrick: [],
      middlePile: [],
      trumpSuit: null,
      isTrumpBroken: false,
      activePenalty: "none",
      lastWinnerId: null,
      lastAction: undefined,
    };

    handleStartGame(resetState);
  };

  const commitRevealStateRef = useRef<{
    mySeed?: number;
    hashes: Record<string, string>;
    seeds: Record<string, number>;
    tempState?: any;
    expectedPeers: string[];
    completed?: boolean;
    fallbackTimer?: any;
  }>({ hashes: {}, seeds: {}, expectedPeers: [] });

  const generateStateWithSeed = (seed: number, state: GameState) => {
    if (state.gameType === "okey") {
      const okeyState = OkeyEngine.distributeTiles({ ...state, seed });
      DeckManager.computeHash(JSON.stringify(okeyState)).then((hash) => {
        okeyState.expectedHash = hash;
        okeyState.seed = seed;
        updateGameState(okeyState);
      });
      return;
    }

    let players = state.players.map((p) => ({ ...p, hand: [...p.hand] }));
    const deck = DeckManager.createDeck(
      selectedSkinId,
      false,
      seed,
    );
    let middlePile: CardType[] = [];

    if (state.gameType === "pisti") {
      middlePile = deck.splice(0, 4);
      middlePile[3] = { ...middlePile[3], isFaceUp: true };
      players.forEach((p) => (p.hand = deck.splice(0, 4)));
    } else {
      if (state.gameType === "batak" && state.batakVariant === "gomulu") {
        middlePile = deck.splice(0, 4);
      }
      const cardsPerPlayer = Math.floor(deck.length / players.length);
      players.forEach((p) => {
        p.hand = DeckManager.sortHand(deck.splice(0, cardsPerPlayer));
      });
      let idx = 0;
      while (deck.length > 0) {
        players[idx % players.length].hand.push(deck.pop()!);
        players[idx % players.length].hand = DeckManager.sortHand(
          players[idx % players.length].hand,
        );
        idx++;
      }
    }

    const isKozmaca =
      state.gameType === "batak" && state.batakVariant === "kozmaca";

    const newState: GameState = {
      ...state,
      hasInitialHiddenCards: state.gameType === "pisti" ? true : false,
      players,
      deck: state.gameType === "pisti" ? deck : [],
      middlePile: middlePile || [],
      currentTrick: [],
      lastAction: undefined,
      status:
        state.gameType === "batak" || state.gameType === "king" || state.gameType === "pisti"
          ? "dealing"
          : "playing",
      currentBidderIndex:
        state.gameType === "batak"
          ? (state.dealerIndex + 1) % players.length
          : undefined,
      currentMaxBid:
        state.gameType === "batak" && state.batakVariant === "esli" ? 7 : 4,
      currentMaxBidderId: null,
      bids: {},
      trumpSuit: isKozmaca ? "spades" : null,
      isTrumpBroken: false,
      turnIndex:
        state.gameType === "batak"
          ? (state.dealerIndex + 1) % players.length
          : state.gameType === "king"
            ? state.dealerIndex
            : 0,
    };

    const finishState = {
      ...newState,
      seed,
    };
    updateGameState(finishState);

    DeckManager.computeHash(JSON.stringify(finishState))
      .then((hash) => {
        finishState.expectedHash = hash;
        updateGameState(finishState);
      })
      .catch((err) => {
        console.warn("computeHash error:", err);
      });
  };

  const checkRevealPhase = async () => {
    const cr = commitRevealStateRef.current;
    if (cr.completed) return;
    if (Object.keys(cr.seeds).length >= cr.expectedPeers.length + 1) {
      cr.completed = true;
      if (cr.fallbackTimer) {
        clearTimeout(cr.fallbackTimer);
      }
      for (const [id, seed] of Object.entries(cr.seeds)) {
        if (cr.hashes[id]) {
          const hash = await DeckManager.computeHash(seed.toString());
          if (hash !== cr.hashes[id]) {
            console.warn(
              `Hash mismatch for player ${id}! Computed: ${hash}, Expected: ${cr.hashes[id]}, Seed: ${seed}`,
            );
            console.warn(
              `Hile Tespit Edildi: Oyuncu seed'i hash ile eşleşmiyor!\nID: ${id}\nHash: ${cr.hashes[id]}`,
            );
            return;
          }
        }
      }
      let rawSum = 0;
      for (const s of Object.values(cr.seeds)) {
        rawSum += Number(s);
      }
      const finalSeed = rawSum % 0xffffffff;
      if (cr.tempState && cr.tempState.players.find((p: any) => !p.isBot)?.id === myId) {
        generateStateWithSeed(finalSeed, cr.tempState);
      }
    }
  };

  const checkCommitPhase = () => {
    const cr = commitRevealStateRef.current;
    if (Object.keys(cr.hashes).length >= cr.expectedPeers.length + 1) {
      if (cr.mySeed !== undefined && cr.seeds[myId] === undefined) {
        networkRef.current?.broadcast("REVEAL_SEED", { seed: cr.mySeed });
        cr.seeds[myId] = cr.mySeed;
        checkRevealPhase();
      }
    }
  };

  const handleStartGame = (currentState?: any) => {
    const state =
      currentState &&
      typeof currentState === "object" &&
      "status" in currentState
        ? currentState
        : gameState;
    if (!state) return;
    
    trackEvent("game_started_actual", state.gameType, state.gameMode);

    if (state.gameType === "okey") {
      soundManager.play("tile_shuffle");
    } else {
      soundManager.play("shuffle");
    }

    let players = [...state.players];
    const maxPlayers =
      state.maxPlayers ||
      (state.gameType === "pisti" && !state.isTeamGame ? 2 : 4);

    while (players.length < maxPlayers) {
      const PREDEFINED_AVATAR_KEYS = ['ahmet', 'alp', 'asli', 'can', 'ece', 'elif', 'emre', 'gizem', 'mehmet', 'ozan', 'selin', 'zeynep'];
      const BOT_NAMES = [
        "Zeynep",
        "Elif",
        "Ece",
        "Asli",
        "Gizem",
        "Selin",
        "Ahmet",
        "Mehmet",
        "Can",
        "Ozan",
        "Emre",
        "Alp",
      ];
      const existingNames = new Set(players.map((p) => p.name.toLowerCase()));
      const existingAvatars = new Set(
        players.map((p) => resolveAvatarUrl(p.avatar || p.name))
      );

      const unusedBotNames = BOT_NAMES.filter((name) => !existingNames.has(name.toLowerCase()));
      const finalNamesList = unusedBotNames.length > 0 ? unusedBotNames : BOT_NAMES;

      const unusedAvatars = PREDEFINED_AVATAR_KEYS.filter(
        (key) => !existingAvatars.has(resolveAvatarUrl(key))
      );
      const finalAvatarPool = unusedAvatars.length > 0 ? unusedAvatars : PREDEFINED_AVATAR_KEYS;

      const perfectMatches = finalNamesList.filter((name) =>
        finalAvatarPool.includes(name.toLowerCase())
      );

      let chosenName: string;
      let chosenAvatarKey: string;

      if (perfectMatches.length > 0) {
        chosenName = perfectMatches[Math.floor(Math.random() * perfectMatches.length)];
        chosenAvatarKey = chosenName.toLowerCase();
      } else {
        chosenName = finalNamesList[Math.floor(Math.random() * finalNamesList.length)];
        chosenAvatarKey = finalAvatarPool[Math.floor(Math.random() * finalAvatarPool.length)];
      }

      const botId = `bot-${players.length}-${Math.random().toString(36).substr(2, 5)}`;
      players.push({
        id: botId,
        name: chosenName,
        avatar: chosenAvatarKey,
        isHost: false,
        isBot: true,
        hand: [],
        score: 0,
        collectedCards: [],
        pistiCount: 0,
        remainingPenalties: [
          "tricks",
          "hearts",
          "girls",
          "boys",
          "king-hearts",
          "last-two",
        ],
        remainingTrumps: 2,
        bid: 0,
      });
    }

    if (state.isTeamGame) {
      const teamA = players.filter((p) => p.team === "A");
      const teamB = players.filter((p) => p.team === "B");
      const unassigned = players.filter((p) => p.team !== "A" && p.team !== "B");

      while (teamA.length < 2 && unassigned.length > 0) {
        teamA.push(unassigned.shift()!);
      }
      while (teamB.length < 2 && unassigned.length > 0) {
        teamB.push(unassigned.shift()!);
      }
      while (teamA.length > 2) {
        teamB.push(teamA.pop()!);
      }
      while (teamB.length > 2) {
        teamA.push(teamB.pop()!);
      }

      teamA.forEach((p) => (p.team = "A"));
      teamB.forEach((p) => (p.team = "B"));

      players = [];
      for (let i = 0; i < 2; i++) {
        if (teamA[i]) players.push(teamA[i]);
        if (teamB[i]) players.push(teamB[i]);
      }
    }

    const updatedState = { ...state, players };

    if (updatedState.networkMode === "P2P") {
      const mySeed = Math.floor(Math.random() * 0xffffffff);
      if (updatedState.gameType === "okey") {
        const isFirstHand = state.status === "waiting";
        const nextTurnCount = isFirstHand ? 0 : (state.turnCount !== undefined ? state.turnCount + 1 : 1);
        const nextDealerIndex = isFirstHand ? 0 : (state.dealerIndex !== undefined ? (state.dealerIndex + 1) % state.players.length : 1);

        generateStateWithSeed(mySeed, {
          ...updatedState,
          turnCount: nextTurnCount,
          dealerIndex: nextDealerIndex,
        });
      } else {
        generateStateWithSeed(mySeed, updatedState);
      }
      return;
    }

    if (updatedState.gameType === "okey") {
      const mySeed = Math.floor(Math.random() * 0xffffffff);
      const isFirstHand = state.status === "waiting";
      const nextTurnCount = isFirstHand ? 0 : (state.turnCount !== undefined ? state.turnCount + 1 : 1);
      const nextDealerIndex = isFirstHand ? 0 : (state.dealerIndex !== undefined ? (state.dealerIndex + 1) % state.players.length : 1);

      generateStateWithSeed(mySeed, {
        ...updatedState,
        turnCount: nextTurnCount,
        dealerIndex: nextDealerIndex,
      });
      return;
    }

    // YEREL DAĞITIM (Mevcut Bot Modları İçin - KESİNLİKLE DOKUNULMADI)
    const deck = DeckManager.createDeck(selectedSkinId);

    if (updatedState.gameType === "pisti") {
      const middlePile = deck.splice(0, 4);
      middlePile[3].isFaceUp = true;
      players.forEach((p) => (p.hand = deck.splice(0, 4)));

      const newState: GameState = {
        ...updatedState,
        seed: Math.random().toString(36).substring(7),
        round: 1,
        players,
        deck,
        middlePile,
        hasInitialHiddenCards: true,
        status: "dealing",
        turnIndex: 0,
        lastAction: undefined,
      };
      updateGameState(newState);
    } else {
      // King / Batak: Deal all cards locally
      let middlePile: CardType[] = [];
      if (updatedState.gameType === "batak" && updatedState.batakVariant === "gomulu") {
        middlePile = deck.splice(0, 4);
      }
      const totalCards = deck.length;
      const cardsPerPlayer = Math.floor(totalCards / players.length);
      players.forEach((p) => (p.hand = deck.splice(0, cardsPerPlayer)));

      let playerIdx = 0;
      while (deck.length > 0) {
        const card = deck.pop();
        if (card) {
          players[playerIdx % players.length].hand.push(card);
          playerIdx++;
        }
      }

      players.forEach((p) => {
        p.hand = DeckManager.sortHand(p.hand);
      });

      const isKozmaca =
        updatedState.gameType === "batak" && updatedState.batakVariant === "kozmaca";

      const newState: GameState = {
        ...updatedState,
        seed: Math.random().toString(36).substring(7),
        round: 1,
        players: players,
        deck: [],
        middlePile: middlePile,
        currentTrick: [],
        status:
          state.gameType === "batak" || state.gameType === "king" || state.gameType === "pisti"
            ? "dealing"
            : "playing",
        currentBidderIndex:
          state.gameType === "batak"
            ? (state.dealerIndex + 1) % players.length
            : undefined,
        currentMaxBid:
          state.gameType === "batak" && state.batakVariant === "esli" ? 7 : 4,
        currentMaxBidderId: null,
        bids: {},
        trumpSuit: isKozmaca ? "spades" : null,
        turnIndex:
          state.gameType === "batak"
            ? (state.dealerIndex + 1) % players.length
            : state.dealerIndex,
      };
      updateGameState(newState);
    }
  };

  const handleBidGame = (bid: number | "pass") => {
    soundManager.play("click");
    setGameState((prev) => {
      if (!prev || prev.status !== "bidding") return null;
      const currentIdx = prev.currentBidderIndex || 0;
      if (!prev.players[currentIdx]) return prev;
      const bidderId = prev.players[currentIdx].id;

      const newBids = { ...(prev.bids || {}), [bidderId]: bid };
      let newMaxBid = prev.currentMaxBid || 4;
      let newMaxBidderId = prev.currentMaxBidderId;

      if (
        prev.batakVariant !== "kozmaca" &&
        typeof bid === "number" &&
        bid > newMaxBid
      ) {
        newMaxBid = bid;
        newMaxBidderId = bidderId;
      }

      // Check if bidding is done
      const passCount = Object.values(newBids).filter(
        (b) => b === "pass",
      ).length;
      const allResponded = Object.keys(newBids).length === prev.players.length;

      let newStatus: GameStatus = prev.status;
      let newTurnIndex = currentIdx;
      let newCurrentBidderIndex = currentIdx;
      let newPlayers = [...prev.players];
      let newMiddlePile = prev.middlePile;

      let newTrumpSuit = prev.trumpSuit;

      if (prev.batakVariant === "kozmaca") {
        newPlayers[currentIdx].bid = typeof bid === "number" ? bid : 1;
        if (allResponded) {
          newStatus = "playing";
          newTurnIndex = (prev.dealerIndex + 1) % prev.players.length;
          // In kozmaca, trump is always spades
          newTrumpSuit = "spades";
        } else {
          newCurrentBidderIndex =
            (newCurrentBidderIndex + 1) % prev.players.length;
        }
      } else if (passCount === prev.players.length - 1 && newMaxBidderId) {
        // bidding won
        if ((prev.batakVariant as string) === "kozmaca") {
          newStatus = "playing";
          newTrumpSuit = "spades";
        } else {
          newStatus =
            prev.batakVariant === "gomulu" ? "discarding" : "selecting"; // The winner selects trump
        }
        newTurnIndex = newPlayers.findIndex((p) => p.id === newMaxBidderId);

        // Update the winner's required bid
        newPlayers[newTurnIndex] = {
          ...newPlayers[newTurnIndex],
          bid: newMaxBid,
        };

        if (prev.batakVariant === "gomulu" && prev.middlePile && prev.middlePile.length > 0) {
          const existingIds = new Set(newPlayers[newTurnIndex].hand.map((c) => c.id));
          const cardsToAdd = prev.middlePile.filter((c) => !existingIds.has(c.id));
          newPlayers[newTurnIndex].hand = DeckManager.sortHand([
            ...newPlayers[newTurnIndex].hand,
            ...cardsToAdd,
          ]);
          newMiddlePile = [];
        }
      } else if (allResponded && !newMaxBidderId) {
        // Everyone passed. Some variants restart or auto to dealer. Let's auto to dealer.
        newMaxBid = prev.batakVariant === "esli" ? 8 : 5;
        newTurnIndex = prev.dealerIndex || 0;
        newMaxBidderId = newPlayers[newTurnIndex]?.id || newPlayers[0].id;
        newPlayers[newTurnIndex] = {
          ...newPlayers[newTurnIndex],
          bid: newMaxBid,
        };

        if ((prev.batakVariant as string) === "kozmaca") {
          newStatus = "playing";
          newTrumpSuit = "spades";
        } else {
          newStatus =
            prev.batakVariant === "gomulu" ? "discarding" : "selecting";
        }

        if (prev.batakVariant === "gomulu" && prev.middlePile && prev.middlePile.length > 0) {
          const existingIds = new Set(newPlayers[newTurnIndex].hand.map((c) => c.id));
          const cardsToAdd = prev.middlePile.filter((c) => !existingIds.has(c.id));
          newPlayers[newTurnIndex].hand = DeckManager.sortHand([
            ...newPlayers[newTurnIndex].hand,
            ...cardsToAdd,
          ]);
          newMiddlePile = [];
        }
      } else {
        // Move to next player that hasn't passed
        do {
          newCurrentBidderIndex =
            (newCurrentBidderIndex + 1) % newPlayers.length;
        } while (
          newPlayers[newCurrentBidderIndex] &&
          newBids[newPlayers[newCurrentBidderIndex].id] === "pass"
        );
      }

      const newState = {
        ...prev,
        players: newPlayers,
        bids: newBids,
        currentMaxBid: newMaxBid,
        currentMaxBidderId: newMaxBidderId,
        status: newStatus,
        trumpSuit: newTrumpSuit,
        isTrumpBroken: newStatus === "playing" ? false : prev.isTrumpBroken,
        turnIndex:
          newStatus === "playing" ||
          newStatus === "selecting" ||
          newStatus === "discarding"
            ? newTurnIndex
            : newCurrentBidderIndex,
        bidderIndex:
          newStatus === "selecting" || newStatus === "discarding"
            ? newTurnIndex
            : prev.bidderIndex,
        currentBidderIndex: newCurrentBidderIndex,
        middlePile: newMiddlePile,
      };

      if (isAmIHost(prev, myId)) {
        broadcastState(newState);
      }

      return newState;
    });
  };

  const handleSelectGame = (type: PenaltyType | Suit, bid?: number) => {
    soundManager.play("click");
    if (gameState?.gameType === "batak") {
      soundManager.play("pisti");
    }
    setGameState((prev) => {
      if (!prev) return null;
      const playerIndex = prev.turnIndex;
      let updatedPlayers = prev.players.map(p => ({ ...p, hand: [...p.hand], collectedCards: [...p.collectedCards], remainingPenalties: [...p.remainingPenalties] }));

      let trumpSuit: Suit | null = null;
      let activePenalty: PenaltyType = "none";

      if (["spades", "hearts", "diamonds", "clubs"].includes(type)) {
        trumpSuit = type as Suit;
        if (prev.gameType === "king") {
          updatedPlayers[playerIndex].remainingTrumps -= 1;
        }
      } else {
        activePenalty = type as PenaltyType;
        const pIdx =
          updatedPlayers[playerIndex].remainingPenalties.indexOf(activePenalty);
        if (pIdx !== -1)
          updatedPlayers[playerIndex].remainingPenalties.splice(pIdx, 1);
      }

      const newState: GameState = {
        ...prev,
        players: updatedPlayers,
        trumpSuit,
        isTrumpBroken: false,
        activePenalty,
        status: "playing",
        turnIndex: playerIndex,
        bidderIndex: prev.gameType === "batak" ? playerIndex : prev.bidderIndex,
      };

      if (isAmIHost(prev, myId))
        broadcastState(newState);
      return newState;
    });
  };

  const handleOkeyDiscard = (playerId: string, tileId: string) => {
    soundManager.play("tile_drop");
    setGameState((prev) => {
      if (!prev || prev.gameType !== "okey") return prev;

      const newState = OkeyEngine.discardTile(prev, playerId, tileId);

      if (isAmIHost(prev, myId)) {
        broadcastState(newState);
      }
      return newState;
    });
  };

  const handleOkeyDraw = (playerId: string, source: "deck" | "discard") => {
    soundManager.play("tile_draw");
    setGameState((prev) => {
      if (!prev || prev.gameType !== "okey") return prev;

      const newState = OkeyEngine.drawTile(prev, playerId, source);

      if (isAmIHost(prev, myId)) {
        broadcastState(newState);
      }
      return newState;
    });
  };

  const handleOkeyToggleDouble = (playerId: string) => {
    setGameState((prev) => {
      if (!prev || prev.gameType !== "okey") return prev;
      const newState = OkeyEngine.toggleDouble(prev, playerId);
      if (isAmIHost(prev, myId)) {
        broadcastState(newState);
      }
      return newState;
    });
  };

  const handleOkeyShowIndicator = (playerId: string) => {
    setGameState((prev) => {
      if (!prev || prev.gameType !== "okey") return prev;
      const newState = OkeyEngine.showIndicator(prev, playerId);
      if (isAmIHost(prev, myId)) {
        broadcastState(newState);
      }
      return newState;
    });
  };

  const handleDealingComplete = useCallback(() => {
    setGameState((prev) => {
      if (!prev || prev.status !== "dealing") return prev;
      const isP2P = prev.networkMode === "P2P";
      const isHost = isAmIHost(prev, myId);

      const hasHumanPeers = prev.players.filter((p) => !p.isBot).length > 1;
      if (isP2P && hasHumanPeers && !isHost) return prev;

      const nextStatus =
        prev.gameType === "batak"
          ? "bidding"
          : prev.gameType === "king"
          ? "selecting"
          : "playing";

      const newState = { ...prev, status: nextStatus as typeof prev.status };
      if (isP2P && isHost) {
        broadcastState(newState);
      }
      return newState;
    });
  }, [myId]);

  useEffect(() => {
    if (gameState?.status === "dealing") {
      const timeoutMs =
        gameState.gameType === "batak" || gameState.gameType === "king"
          ? 4800
          : gameState.gameType === "okey"
          ? 2800
          : 1800;
      const safetyTimer = setTimeout(() => {
        setGameState((prev) => {
          if (prev && prev.status === "dealing") {
            const fallbackStatus =
              prev.gameType === "batak"
                ? "bidding"
                : prev.gameType === "king"
                ? "selecting"
                : "playing";
            const newState = { ...prev, status: fallbackStatus as typeof prev.status };
            if (isAmIHost(prev, myId)) {
              broadcastState(newState);
            }
            return newState;
          }
          return prev;
        });
      }, timeoutMs);
      return () => clearTimeout(safetyTimer);
    }
  }, [gameState?.status, gameState?.gameType, myId]);

  const handleChatMessage = (playerId: string, text: string) => {
    // Check if the text is one of our special emojis
    const matchedEmoji = READY_EMOJIS.find(r => r.emoji === text);
    if (matchedEmoji) {
      // Find the player's avatar on screen
      let spawnX = window.innerWidth / 2;
      let spawnY = window.innerHeight / 2;
      const playerEl = document.querySelector(`[data-player-id="${playerId}"]`);
      if (playerEl) {
        const rect = playerEl.getBoundingClientRect();
        spawnX = rect.left + rect.width / 2;
        spawnY = rect.top + rect.height / 2;
      }
      
      const foundPlayer = gameState?.players.find(p => p.id === playerId);
      
      // Dispatch global burst event
      window.dispatchEvent(new CustomEvent('spawn-emoji-burst', { 
        detail: { 
          emoji: matchedEmoji.emoji, 
          x: spawnX, 
          y: spawnY, 
          label: matchedEmoji.name,
          targetName: foundPlayer?.name || "Oyuncu",
          sound: matchedEmoji.sound
        } 
      }));
    }

    setChatMessages((prev) => ({ ...prev, [playerId]: text }));
    setTimeout(() => {
      setChatMessages((prev) => {
        const newMsg = { ...prev };
        if (newMsg[playerId] === text) {
          delete newMsg[playerId];
        }
        return newMsg;
      });
    }, 3000);
  };

  const handlePlayCard = (
    cardId: string,
    playerId: string,
    isBluff: boolean = false,
    fromRemotePeer: boolean = false,
  ) => {
    const currentState = gameStateRef.current || gameState;
    if (!currentState) return;

    let actingPlayerId = playerId;
    if (currentState.gameType === "batak" && currentState.batakVariant === "esli" && currentState.bidderIndex !== undefined) {
      const bidderId = currentState.players[currentState.bidderIndex].id;
      const partnerIndex = (currentState.bidderIndex + 2) % 4;
      if (playerId === bidderId && currentState.turnIndex === partnerIndex) {
        actingPlayerId = currentState.players[partnerIndex].id;
      }
    }

    // Check validity: Kart dağıtımı bitmeden veya sıra başlamadan yere kart atılamaz
    if (currentState.status === "dealing" || currentState.status === "waiting" || currentState.status === "selecting") {
      console.warn("Kart dağıtımı bitmeden yere kart atılamaz!");
      return;
    }

    let player = currentState.players.find((p) => isSamePlayerId(p.id, actingPlayerId));
    if (!player && userName) {
      player = currentState.players.find((p) => !p.isBot && p.name && p.name.trim().toLowerCase() === userName.trim().toLowerCase());
      if (player) actingPlayerId = player.id;
    }
    if (!player && currentState.turnIndex !== undefined && currentState.players[currentState.turnIndex]) {
      player = currentState.players[currentState.turnIndex];
      actingPlayerId = player.id;
    }
    if (!player) return;

    // Pişti: Masaya düşmeden ve löve toplanmadan arka arkaya hamle engeli (Sadece tek oyunculu bot maçlarında akıcılık için)
    if (!fromRemotePeer && currentState.gameType === "pisti" && currentState.lastAction && currentState.networkMode === "AI") {
      const { type, timestamp } = currentState.lastAction;
      const elapsed = Math.max(0, Date.now() - (timestamp || 0));
      let requiredLock = 0;
      if (type === "play") {
        requiredLock = 0;
      } else if (type === "capture" || type === "pisti") {
        requiredLock = type === "pisti" ? (gameSpeed === "fast" ? 500 : 650) : (gameSpeed === "fast" ? 300 : 400);
      }

      if (elapsed < requiredLock) {
        const retryDelay = Math.max(10, requiredLock - elapsed);
        setTimeout(() => {
          handlePlayCard(cardId, playerId, isBluff, fromRemotePeer);
        }, retryDelay);
        return;
      }
    }

    const currentTurnPlayer = currentState.players[currentState.turnIndex];
    const isPlayerTurn = currentTurnPlayer && (
      isSamePlayerId(currentTurnPlayer.id, actingPlayerId) ||
      (!currentTurnPlayer.isBot && userName && currentTurnPlayer.name && currentTurnPlayer.name.trim().toLowerCase() === userName.trim().toLowerCase())
    );

    if (
      currentState.status !== "discarding" &&
      !isPlayerTurn
    ) {
      console.warn(
        `Hile Tespit Edildi! Sırada olmayan oyuncu hamle yaptı: ${actingPlayerId}`,
      );
      return;
    }

    const hasCard = player.hand.some((c) => c.id === cardId);
    if (!hasCard) {
      console.error(
        `Hile Tespit Edildi! Oyuncu elinde olmayan kartı oynadı: ${cardId}`,
      );
      return;
    }

    let trickForValidation = currentState.currentTrick;
    if (trickForValidation.length === currentState.players.length) {
      trickForValidation = [];
    }

    let isValid = true;
    if (currentState.status === "discarding") {
      isValid = true;
    } else if (currentState.gameType === "batak") {
      isValid = BatakEngine.getValidCards(
        player.hand,
        trickForValidation,
        currentState.trumpSuit,
        currentState.isTrumpBroken ?? false,
      ).some((c) => c.id === cardId);
    } else if (currentState.gameType === "king") {
      isValid = KingEngine.getValidCards(
        player.hand,
        trickForValidation,
        currentState.trumpSuit,
        currentState.activePenalty,
        currentState.isTrumpBroken ?? false,
      ).some((c) => c.id === cardId);
    }
    if (!isValid) {
      if (isSamePlayerId(playerId, myId)) {
        soundManager.play("error");
      }
      if (isAmIHost(currentState, myId)) {
        broadcastState(currentState);
      }
      return;
    }

    soundManager.play("cardPlay");

    setGameState((prev) => {
      if (!prev) return null;
      let playerIndex = prev.players.findIndex((p) => p.id === playerId);
      if (playerIndex === -1) {
        playerIndex = prev.players.findIndex((p) => p.id && playerId && (p.id.includes(playerId) || playerId.includes(p.id)));
      }
      if (playerIndex === -1 && prev.turnIndex !== undefined && prev.players[prev.turnIndex]) {
        playerIndex = prev.turnIndex;
      }
      if (playerIndex === -1) return prev;

      const player = prev.players[playerIndex];
      const card = player.hand.find((c) => c.id === cardId);
      if (!card) return prev;

      if (prev.status === "discarding") {
        const newHand = player.hand.filter((c) => c.id !== cardId);
        const discardedCard = player.hand.find((c) => c.id === cardId);
        let updatedPlayers = prev.players.map(p => ({ ...p, hand: [...p.hand], collectedCards: [...p.collectedCards], remainingPenalties: [...p.remainingPenalties] }));
        updatedPlayers[playerIndex].hand = newHand;

        const discardedCardObj = { ...discardedCard!, isFaceUp: false };
        const newMiddlePile = [...prev.middlePile, discardedCardObj];

        // The target hand size is what they originally had (i.e. (52 - 4) / players.length)
        const targetHandSize = Math.floor(48 / prev.players.length);
        const isDoneDiscarding = newHand.length === targetHandSize;

        if (isDoneDiscarding) {
          updatedPlayers[playerIndex].collectedCards.push(...newMiddlePile);
        }

        const newState: GameState = {
          ...prev,
          players: updatedPlayers,
          middlePile: isDoneDiscarding ? [] : newMiddlePile,
          status: isDoneDiscarding ? "selecting" : "discarding",
          lastAction: {
            type: isDoneDiscarding ? "capture" : "play",
            playerId,
            card: discardedCardObj,
            capturedPile: isDoneDiscarding ? newMiddlePile : undefined,
            timestamp: Date.now(),
          },
          lastWinnerId: isDoneDiscarding ? playerId : prev.lastWinnerId,
        };
        if (isAmIHost(prev, myId))
          broadcastState(newState);
        return newState;
      }

      const newHand = player.hand.filter((c) => c.id !== cardId);
      let nextMiddlePile = [...prev.middlePile];

      let lastWinnerId = prev.lastWinnerId;
      let lastAction: GameState["lastAction"] = {
        type: "play",
        playerId,
        timestamp: Date.now(),
      };
      let nextTurnIndex = (prev.turnIndex + 1) % prev.players.length;

      if (prev.gameType === "pisti") {
        if (prev.bluffingMode && isBluff && prev.middlePile.length > 0) {
          nextMiddlePile.push({ ...card, isFaceUp: false }); // Face down bluff card

          let updatedPlayers = prev.players.map(p => ({ ...p, hand: [...p.hand], collectedCards: [...p.collectedCards], remainingPenalties: [...p.remainingPenalties] }));
          updatedPlayers[playerIndex].hand = newHand;

          const newState: GameState = {
            ...prev,
            players: updatedPlayers,
            middlePile: nextMiddlePile,
            status: "bluff_challenge",
            turnIndex: nextTurnIndex,
            activeBluff: {
              card,
              playerId,
              pile: nextMiddlePile,
            },
            lastAction: { type: "play", playerId, timestamp: Date.now() },
          };
          if (isAmIHost(prev, myId)) {
            broadcastState(newState);
          }
          return newState;
        } else {
          nextMiddlePile.push({ ...card, isFaceUp: true });
        }
      }

      let nextIsTrumpBroken = prev.isTrumpBroken ?? false;
      if (card) {
        if (prev.trumpSuit && card.suit === prev.trumpSuit) {
          nextIsTrumpBroken = true;
        } else if (
          prev.gameType === "king" &&
          (prev.activePenalty === "hearts" || prev.activePenalty === "king-hearts") &&
          card.suit === "hearts"
        ) {
          nextIsTrumpBroken = true;
        }
      }

      let updatedPlayers = prev.players.map(p => ({ ...p, hand: [...p.hand], collectedCards: [...p.collectedCards], remainingPenalties: [...p.remainingPenalties] }));
      updatedPlayers[playerIndex].hand = newHand;

      let nextTrick =
        prev.currentTrick.length === prev.players.length
          ? [{ card, playerId }]
          : [...prev.currentTrick, { card, playerId }];

      if (prev.gameType === "pisti") {
        if (nextMiddlePile.length >= 2) {
          const isCapture = PistiEngine.canCapture(
            card,
            nextMiddlePile.slice(0, -1),
          );
          if (isCapture) {
            const isPisti = PistiEngine.checkPisti(
              card,
              nextMiddlePile.slice(0, -1),
            );
            const hadHidden =
              (prev.hasInitialHiddenCards ??
                prev.players.every((p) => (p.collectedCards?.length || 0) === 0)) &&
              nextMiddlePile.length >= 4;
            const initialHiddenCards = hadHidden
              ? nextMiddlePile.slice(0, 3).map((c) => ({ ...c, isFaceUp: true }))
              : undefined;

            updatedPlayers[playerIndex].collectedCards.push(...nextMiddlePile);
            if (isPisti) {
              const pistiValue = card.rank === "J" ? 2 : 1;
              // Pişti
              updatedPlayers[playerIndex].pistiCount += pistiValue;
              soundManager.play("pisti");
              lastAction = {
                type: "pisti",
                playerId,
                card,
                capturedPile: [...nextMiddlePile],
                timestamp: Date.now(),
                hadInitialHiddenCards: hadHidden,
                initialHiddenCards,
              };
              confetti({
                particleCount: playerId === myId ? 110 : 75,
                spread: 75,
                origin: { y: playerId === myId ? 0.7 : 0.35 },
              });
            } else {
              soundManager.play("collect");
              lastAction = {
                type: "capture",
                playerId,
                card,
                capturedPile: [...nextMiddlePile],
                timestamp: Date.now(),
                hadInitialHiddenCards: hadHidden,
                initialHiddenCards,
              };
            }
            nextMiddlePile = [];
            lastWinnerId = playerId;
          }
        }
      } else if (prev.gameType === "king") {
        if (nextTrick.length === prev.players.length) {
          const ledSuit = nextTrick[0].card.suit;
          lastWinnerId = KingEngine.determineWinner(
            nextTrick,
            ledSuit,
            prev.trumpSuit,
          );
          soundManager.play("collect");
          const winnerIdx = updatedPlayers.findIndex(
            (p) => p.id === lastWinnerId,
          );

          // Collect cards and calculate interim penalty if applicable
          updatedPlayers[winnerIdx].collectedCards.push(
            ...nextTrick.map((t) => t.card),
          );

          const isLastTrick = updatedPlayers.every((p) => p.hand.length === 0);
          const isSecondToLastTrick = updatedPlayers.every(
            (p) => p.hand.length === 1,
          );
          if (prev.activePenalty !== "none") {
            const penalty = KingEngine.calculatePenalty(
              nextTrick.map((t) => t.card),
              prev.activePenalty,
              isLastTrick,
              isSecondToLastTrick,
            );
            updatedPlayers[winnerIdx].score += penalty;
          }

          lastAction = {
            type: "capture",
            playerId: lastWinnerId,
            capturedPile: nextTrick.map((t) => t.card),
            trick: [...nextTrick],
            timestamp: Date.now(),
          };
          nextTrick = [];

          // Winner of trick starts next
          nextTurnIndex = winnerIdx;
        }
      } else if (prev.gameType === "batak") {
        if (nextTrick.length === prev.players.length) {
          const ledSuit = nextTrick[0].card.suit;
          lastWinnerId = BatakEngine.determineWinner(
            nextTrick,
            ledSuit,
            prev.trumpSuit,
          );
          soundManager.play("collect");
          const winnerIdx = updatedPlayers.findIndex(
            (p) => p.id === lastWinnerId,
          );
          updatedPlayers[winnerIdx].collectedCards.push(
            ...nextTrick.map((t) => t.card),
          );

          lastAction = {
            type: "capture",
            playerId: lastWinnerId,
            capturedPile: nextTrick.map((t) => t.card),
            trick: [...nextTrick],
            timestamp: Date.now(),
          };
          nextTrick = [];

          nextTurnIndex = winnerIdx;
        }
      }

      let nextStatus = prev.status;
      let nextDeck = [...prev.deck];

      const allHandsEmpty = updatedPlayers.every((p) => p.hand && p.hand.length === 0);
      if (allHandsEmpty) {
        if (nextDeck.length > 0) {
          if (prev.gameType === "pisti") {
            // Pişti: Oyuncuların ellerindeki kartlar bitmeden kart dağıtımı ASLA yapılmaz!
            // Atılan 4. kartın masaya inişi, löve / pişti toplanma animasyonu ve ses efektleri
            // tamamen sonuçlandıktan sonra yeni 4'er kart dağıtılır.
            const isPisti = lastAction?.type === "pisti";
            const isCapture = lastAction?.type === "capture";
            const dealDelay = isPisti
              ? (gameSpeed === "fast" ? 1450 : 1800)
              : isCapture
              ? (gameSpeed === "fast" ? 1150 : 1400)
              : 800;

            setTimeout(() => {
              setGameState((current) => {
                if (!current || current.gameType !== "pisti") return current;
                // In P2P with multiple humans, only the Host holds the unmasked deck and deals
                const hasMultipleHumans = current.players.filter((p) => !p.isBot).length > 1;
                if (current.networkMode === "P2P" && hasMultipleHumans && !isAmIHost(current, myId)) {
                  return current;
                }
                // KESİN ŞART: Oyuncuların elinde kart kalmadığından emin olunmalı
                const stillAllEmpty = current.players.every(
                  (p) => !p.hand || p.hand.length === 0
                );
                if (!stillAllEmpty) {
                  console.warn("Kart dağıtımı iptal: Oyuncuların elinde hala kart var!");
                  return current;
                }
                if (!current.deck || current.deck.length === 0) return current;

                const freshDeck = [...current.deck];
                const dealSize = Math.min(
                  4,
                  Math.floor(freshDeck.length / current.players.length),
                );
                const refilledPlayers = current.players.map((p) => ({
                  ...p,
                  hand: freshDeck.splice(0, dealSize),
                }));

                const refilledState: GameState = {
                  ...current,
                  deck: freshDeck,
                  players: refilledPlayers,
                  status: "dealing",
                  lastAction: undefined,
                };

                if (isAmIHost(current, myId)) {
                  broadcastState(refilledState);
                }
                return refilledState;
              });
            }, dealDelay);
          } else {
            const dealSize = Math.min(
              4,
              Math.floor(nextDeck.length / updatedPlayers.length),
            );
            updatedPlayers = updatedPlayers.map((p) => ({ ...p, hand: nextDeck.splice(0, dealSize) }));

            // Distribute any absolute remainder (only for King/Batak which deal full deck)
            let pIdx = 0;
            while (nextDeck.length > 0) {
              const extra = nextDeck.pop();
              if (extra) {
                updatedPlayers[pIdx % updatedPlayers.length].hand.push(extra);
                pIdx++;
              }
            }

            updatedPlayers = updatedPlayers.map((p) => ({ ...p, hand: DeckManager.sortHand(p.hand) }));
          }
        } else {
          // El / Tur Bitişi (Batak, King veya Pişti son el):
          // Kartın masaya inişi, 4 kartın masada kalması ve lövenin kazananın önüne toplanması
          // animasyonlarının görünmesi için puanlama ve yeni el/bitiş geçişi gecikmeli yapılır!
          const isKingOrBatak = prev.gameType === "batak" || prev.gameType === "king";
          const endDelay = isKingOrBatak
            ? (gameSpeed === "fast" ? 1400 : 1750)
            : (gameSpeed === "fast" ? 1200 : 1500);

          setTimeout(() => {
            setGameState((current) => {
              if (!current) return current;
              // In P2P with multiple humans, only the Host calculates final round transition & new deck
              const hasMultipleHumans = current.players.filter((p) => !p.isBot).length > 1;
              if (current.networkMode === "P2P" && hasMultipleHumans && !isAmIHost(current, myId)) {
                return current;
              }
              // KESİN ŞART: Tur halen oynanıyor olmalı ve tüm eller boş olmalı
              if (current.status !== "playing") return current;
              const stillAllEmpty = current.players.every(
                (p) => !p.hand || p.hand.length === 0
              );
              if (!stillAllEmpty) return current;

              let updatedPlayers = current.players.map((p) => ({
                ...p,
                hand: [...p.hand],
                collectedCards: [...p.collectedCards],
                remainingPenalties: [...p.remainingPenalties],
              }));
              let nextMiddlePile = [...current.middlePile];
              let nextStatus: GameState["status"] = current.status;
              let nextDeck = [...current.deck];
              let nextTurnIndex = current.turnIndex;

              if (current.gameType === "pisti") {
                // Return remaining middle cards to last winner (or dealer if no tricks taken)
                if (nextMiddlePile.length > 0) {
                  const targetWinnerId =
                    current.lastWinnerId ||
                    updatedPlayers[current.dealerIndex]?.id ||
                    updatedPlayers[0]?.id;
                  const winnerIdx = updatedPlayers.findIndex(
                    (p) => p.id === targetWinnerId,
                  );
                  if (winnerIdx !== -1) {
                    updatedPlayers[winnerIdx].collectedCards.push(
                      ...nextMiddlePile,
                    );
                    nextMiddlePile = [];
                  }
                }

                if (current.isTeamGame) {
                  const teamACards = updatedPlayers
                    .filter((p) => p.team === "A")
                    .flatMap((p) => p.collectedCards);
                  const teamAPistis = updatedPlayers
                    .filter((p) => p.team === "A")
                    .reduce((acc, p) => acc + p.pistiCount, 0);
                  const teamBCards = updatedPlayers
                    .filter((p) => p.team === "B")
                    .flatMap((p) => p.collectedCards);
                  const teamBPistis = updatedPlayers
                    .filter((p) => p.team === "B")
                    .reduce((acc, p) => acc + p.pistiCount, 0);

                  const isAMajority = teamACards.length > 26;
                  const isBMajority = teamBCards.length > 26;

                  const scoreA = PistiEngine.calculateScore(
                    teamACards,
                    teamAPistis,
                    isAMajority,
                  );
                  const scoreB = PistiEngine.calculateScore(
                    teamBCards,
                    teamBPistis,
                    isBMajority,
                  );

                  updatedPlayers = updatedPlayers.map((p) => ({
                    ...p,
                    score: p.score + (p.team === "A" ? scoreA : scoreB),
                  }));
                } else {
                  const maxCardCount = Math.max(
                    ...updatedPlayers.map((p) => p.collectedCards.length),
                  );
                  const topCollecters = updatedPlayers.filter(
                    (p) => p.collectedCards.length === maxCardCount,
                  );
                  const hasUniqueMajority =
                    topCollecters.length === 1 && maxCardCount > 0;

                  updatedPlayers.forEach((p) => {
                    const isMajorityWinner =
                      hasUniqueMajority &&
                      p.collectedCards.length === maxCardCount;
                    p.score += PistiEngine.calculateScore(
                      p.collectedCards,
                      p.pistiCount,
                      isMajorityWinner,
                    );
                  });
                }

                if (current.targetScore) {
                  if (updatedPlayers.some((p) => p.score >= current.targetScore!)) {
                    nextStatus = "finished";
                  }
                } else {
                  nextStatus = "finished";
                }
              } else if (current.gameType === "king") {
                if (current.trumpSuit) {
                  updatedPlayers = updatedPlayers.map((p) => {
                    const tricksWon = Math.floor(
                      p.collectedCards.length / current.players.length,
                    );
                    if (p.id === myId && tricksWon >= 4) {
                      soundManager.play("pisti");
                    }
                    return {
                      ...p,
                      score:
                        p.score + KingEngine.calculateTrumpScore(tricksWon),
                    };
                  });
                } else if (current.activePenalty !== "none") {
                  const myPlayer = updatedPlayers.find((p) => p.id === myId);
                  if (
                    myPlayer &&
                    myPlayer.score ===
                      current.players.find((p) => p.id === myId)!.score
                  ) {
                    soundManager.play("pisti"); // Didn't get penalty
                  }
                }

                if (
                  updatedPlayers.every(
                    (p) =>
                      (p.remainingPenalties.length <= 3 &&
                        p.remainingTrumps === 0) ||
                      6 - p.remainingPenalties.length + (2 - p.remainingTrumps) >= 5,
                  )
                ) {
                  nextStatus = "finished";
                }
              } else if (current.gameType === "batak") {
                if (current.batakVariant === "esli") {
                  const getTeamTricks = (team: string) =>
                    updatedPlayers
                      .filter((pl) => pl.team === team)
                      .reduce(
                        (acc, pl) =>
                          acc +
                          Math.floor(
                            pl.collectedCards.length / current.players.length,
                          ),
                        0,
                      );
                  const teamATricks = getTeamTricks("A");
                  const teamBTricks = getTeamTricks("B");

                  const winningPlayer =
                    (current.currentMaxBidderId
                      ? updatedPlayers.find((pl) => pl.id === current.currentMaxBidderId)
                      : undefined) ||
                    updatedPlayers[current.bidderIndex ?? 0] ||
                    updatedPlayers[0];
                  const winningTeam = winningPlayer?.team || "A";
                  const winningTeamBid =
                    winningPlayer?.bid || current.currentMaxBid || 8;

                  const scoreA = BatakEngine.calculateScore(
                    winningTeam === "A" ? winningTeamBid : 0,
                    teamATricks,
                    winningTeam === "A",
                    "esli",
                  );
                  const scoreB = BatakEngine.calculateScore(
                    winningTeam === "B" ? winningTeamBid : 0,
                    teamBTricks,
                    winningTeam === "B",
                    "esli",
                  );

                  updatedPlayers.forEach((p) => {
                    if (p.team === "A") p.score += scoreA;
                    else p.score += scoreB;
                  });
                } else {
                  updatedPlayers = updatedPlayers.map((p, idx) => {
                    const tricksWon = Math.floor(
                      p.collectedCards.length / current.players.length,
                    );
                    const isBidWinner = current.currentMaxBidderId
                      ? p.id === current.currentMaxBidderId
                      : idx === current.bidderIndex;
                    const effectiveBid =
                      p.bid !== undefined && p.bid > 0
                        ? p.bid
                        : isBidWinner && current.currentMaxBid
                        ? current.currentMaxBid
                        : 5;
                    if (isBidWinner && tricksWon === effectiveBid) {
                      soundManager.play("pisti");
                    }
                    return {
                      ...p,
                      score:
                        p.score +
                        BatakEngine.calculateScore(
                          effectiveBid,
                          tricksWon,
                          isBidWinner,
                          current.batakVariant || "ihaleli",
                        ),
                    };
                  });
                }

                if (current.targetScore) {
                  if (
                    updatedPlayers.some(
                      (p) =>
                        p.score >= current.targetScore! ||
                        p.score <= -current.targetScore!,
                    )
                  ) {
                    nextStatus = "finished";
                  }
                } else {
                  nextStatus = "finished";
                }
              } else {
                updatedPlayers = updatedPlayers.map((p) => ({
                  ...p,
                  score:
                    p.score +
                    Math.floor(
                      p.collectedCards.length / current.players.length,
                    ),
                }));
                nextStatus = "finished";
              }

              if (nextStatus === "finished") {
                const maxScore = Math.max(...updatedPlayers.map((p) => p.score));
                const myPlayer = updatedPlayers.find((p) => p.id === myId);
                if (myPlayer && myPlayer.score === maxScore) {
                  soundManager.play("win");
                } else {
                  soundManager.play("lose");
                }

                const finishedState: GameState = {
                  ...current,
                  players: updatedPlayers,
                  status: "finished",
                  middlePile: nextMiddlePile,
                  currentTrick: [],
                  lastAction: undefined,
                };
                if (isAmIHost(current, myId)) {
                  broadcastState(finishedState);
                }
                return finishedState;
              }

              // Setup Next Round
              const newDeck = DeckManager.createDeck(selectedSkinId);
              updatedPlayers = updatedPlayers.map((p) => ({
                ...p,
                collectedCards: [],
                pistiCount: 0,
                bid: 0,
              }));
              const newDealerIndex =
                (current.dealerIndex + 1) % current.players.length;

              if (current.gameType === "pisti") {
                nextMiddlePile = newDeck.splice(0, 4);
                nextMiddlePile[3].isFaceUp = true;
                updatedPlayers.forEach((p) => (p.hand = newDeck.splice(0, 4)));
                nextStatus = "dealing";
                nextTurnIndex = (newDealerIndex + 1) % current.players.length;
                nextDeck = newDeck;
              } else {
                if (
                  current.gameType === "batak" &&
                  current.batakVariant === "gomulu"
                ) {
                  nextMiddlePile = newDeck.splice(0, 4);
                } else {
                  nextMiddlePile = [];
                }
                const cardsPerPlayer = Math.floor(
                  newDeck.length / updatedPlayers.length,
                );
                updatedPlayers = updatedPlayers.map((p) => ({
                  ...p,
                  hand: newDeck.splice(0, cardsPerPlayer),
                }));
                let playerIdx = 0;
                while (newDeck.length > 0) {
                  const card = newDeck.pop();
                  if (card) {
                    updatedPlayers[playerIdx % updatedPlayers.length].hand.push(
                      card,
                    );
                    playerIdx++;
                  }
                }
                updatedPlayers = updatedPlayers.map((p) => ({
                  ...p,
                  hand: DeckManager.sortHand(p.hand),
                  bid: 0,
                  collectedCards: [],
                }));
                nextStatus =
                  (current.gameType as string) === "batak" ||
                  (current.gameType as string) === "king" ||
                  (current.gameType as string) === "pisti"
                    ? "dealing"
                    : "playing";
                nextTurnIndex =
                  current.gameType === "king"
                    ? newDealerIndex
                    : (newDealerIndex + 1) % current.players.length;
                nextDeck = [];
              }

              const nextRoundState: GameState = {
                ...current,
                hasInitialHiddenCards: current.gameType === "pisti" ? true : false,
                round: (current.round || 1) + 1,
                seed: Math.floor(Math.random() * 0xffffffff),
                players: updatedPlayers,
                middlePile: nextMiddlePile,
                currentTrick: [],
                turnIndex: nextTurnIndex,
                dealerIndex: newDealerIndex,
                bidderIndex: undefined,
                currentBidderIndex:
                  current.gameType === "batak" ? nextTurnIndex : undefined,
                currentMaxBid:
                  current.gameType === "batak" &&
                  current.batakVariant === "esli"
                    ? 7
                    : 4,
                currentMaxBidderId: null,
                bids: {},
                trumpSuit:
                  current.batakVariant === "kozmaca" ? "spades" : null,
                isTrumpBroken: false,
                activePenalty: "none",
                lastWinnerId: null,
                lastAction: undefined,
                status: nextStatus as
                  | "playing"
                  | "selecting"
                  | "bidding"
                  | "finished",
                deck: nextDeck,
              };

              soundManager.play("shuffle");
              if (isAmIHost(current, myId)) broadcastState(nextRoundState);
              return nextRoundState;
            });
          }, endDelay);
        }
      }

      if (nextStatus === "finished" && prev.status !== "finished") {
        const winnerId = [...updatedPlayers].sort(
          (a, b) => b.score - a.score,
        )[0].id;
        if (winnerId === myId) {
          soundManager.play("win");
        } else {
          soundManager.play("lose");
        }
      }

      const newState: GameState = {
        ...prev,
        hasInitialHiddenCards:
          prev.gameType === "pisti"
            ? nextMiddlePile.length === 0
              ? false
              : prev.hasInitialHiddenCards ?? true
            : false,
        players: updatedPlayers,
        middlePile: nextMiddlePile,
        currentTrick: nextTrick,
        turnIndex: nextTurnIndex,
        isTrumpBroken: nextIsTrumpBroken,
        lastWinnerId,
        lastAction,
        status: nextStatus,
        deck: nextDeck,
      };

      if (isAmIHost(prev, myId))
        broadcastState(newState);
      return newState;
    });
  };

  useEffect(() => {
    const botControllerId = gameState?.players.find((p) => !p.isBot)?.id;

    // Dynamic speed based delays:
    // normal: ~750ms + 300ms
    // fast: ~350ms + 200ms
    const playDelay =
      gameSpeed === "fast"
        ? 350 + Math.random() * 200
        : 750 + Math.random() * 300;

    const challengeDelay =
      gameSpeed === "fast"
        ? 400 + Math.random() * 200
        : 800 + Math.random() * 350;

    const bidDelay =
      gameSpeed === "fast"
        ? 350 + Math.random() * 200
        : 750 + Math.random() * 300;

    const discardDelay =
      gameSpeed === "fast"
        ? 350 + Math.random() * 200
        : 750 + Math.random() * 300;

    const selectDelay = gameSpeed === "fast" ? 350 : 700;

    if (gameState?.status === "playing") {
      const currentPlayer = gameState.players[gameState.turnIndex];
      
      let isPartnerOfHumanBidder = false;
      // Disabled by user request: Bots will now play their own cards in esli batak
      /*
      if (gameState.gameType === "batak" && gameState.batakVariant === "esli" && gameState.bidderIndex !== undefined) {
         const bidder = gameState.players[gameState.bidderIndex];
         const partnerIndex = (gameState.bidderIndex + 2) % 4;
         if (gameState.turnIndex === partnerIndex && !bidder.isBot) {
             isPartnerOfHumanBidder = true;
         }
      }
      */

      if (currentPlayer?.isBot && isAmIHost(gameState, myId) && !isPartnerOfHumanBidder) {
        let effectiveDelay = playDelay;

        if (gameState.gameType === "pisti" && gameState.lastAction) {
          const actionTs = gameState.lastAction.timestamp || 0;
          const elapsed = Date.now() - actionTs;
          if (gameState.lastAction.type === "pisti") {
            const required = gameSpeed === "fast" ? 1240 : 1440;
            const remaining = Math.max(0, required - elapsed);
            effectiveDelay = remaining + (gameSpeed === "fast" ? 120 : 250);
          } else if (gameState.lastAction.type === "capture") {
            const required = gameSpeed === "fast" ? 940 : 1090;
            const remaining = Math.max(0, required - elapsed);
            effectiveDelay = remaining + (gameSpeed === "fast" ? 120 : 250);
          } else if (gameState.lastAction.type === "play") {
            // Masaya düşmeden: kart masaya 300ms'de iner.
            // Önceki kart masaya indikten sonra bot doğal bir düşünme süresi ekleyerek akıcı şekilde oynar.
            const requiredLanding = 330;
            const remaining = Math.max(0, requiredLanding - elapsed);
            const botThinking = gameSpeed === "fast" ? 260 + Math.random() * 140 : 560 + Math.random() * 260;
            effectiveDelay = remaining + botThinking;
          }
        } else {
          const isRecentCapture =
            (gameState.lastAction?.type === "capture" ||
              gameState.lastAction?.type === "pisti") &&
            Date.now() - (gameState.lastAction.timestamp || 0) < 1500;

          const isStartOfPistiRound =
            gameState.gameType === "pisti" &&
            (!gameState.currentTrick || gameState.currentTrick.length === 0) &&
            gameState.players.every((p) => (p.hand?.length || 0) === 4);

          effectiveDelay = isRecentCapture
            ? Math.max(playDelay + 800, 1200)
            : isStartOfPistiRound
            ? Math.max(playDelay, 1000)
            : playDelay;
        }

        const timer = setTimeout(() => {
          if (gameState.gameType === "okey") {
            const rackLen = currentPlayer.okeyState?.rack?.length ?? 0;
            if (rackLen <= 14) {
              const drawFromDiscard = AIService.shouldOkeyDrawFromDiscard(gameState, currentPlayer);
              handleOkeyDraw(currentPlayer.id, drawFromDiscard ? "discard" : "deck");
            } else {
              const discardId = AIService.getBestOkeyDiscard(gameState, currentPlayer);
              handleOkeyDiscard(currentPlayer.id, discardId);

              // Maybe a random message from bot sometimes
              if (Math.random() > 0.8 && gameState.players.filter((p) => !p.isBot).length > 1) {
                const messages = [
                  "Harika taş geldi",
                  "Bitti bitiyor",
                  "Taş vermeyin :)",
                  "Bu el bende",
                  "Okey atarım bak",
                  "Seri atalım arkadaşlar",
                ];
                const msg =
                  messages[Math.floor(Math.random() * messages.length)];
                handleChatMessage(currentPlayer.id, msg);
                networkRef.current?.broadcast("CHAT_MESSAGE", { text: msg });
              }
            }
          } else if (currentPlayer.hand && currentPlayer.hand.length > 0) {
            try {
              const bestCardId = AIService.getBestMove(
                gameState,
                currentPlayer,
              );
              const isBluff = AIService.shouldBotBluff(
                gameState,
                currentPlayer,
                bestCardId,
              );
              handlePlayCard(bestCardId, currentPlayer.id, isBluff);
            } catch (err) {
              console.error("AI Error:", err);
            }
          }
        }, effectiveDelay);
        return () => clearTimeout(timer);
      }
    }

    if (gameState?.status === "bluff_challenge") {
      const currentPlayer = gameState.players[gameState.turnIndex];
      if (currentPlayer?.isBot && isAmIHost(gameState, myId)) {
        const timer = setTimeout(() => {
          const isChallenge = AIService.shouldChallengeBluff(
            gameState,
            currentPlayer,
          );
          handleResolveBluff(currentPlayer.id, isChallenge);
        }, challengeDelay);
        return () => clearTimeout(timer);
      }
    }

    if (gameState?.status === "bidding") {
      const currentPlayer =
        gameState.players[gameState.currentBidderIndex || 0];
      if (currentPlayer?.isBot && isAmIHost(gameState, myId)) {
        const isInitialDeal = Object.keys(gameState.bids || {}).length === 0;
        const effectiveBidDelay = isInitialDeal ? bidDelay + 2100 : bidDelay;
        const timer = setTimeout(() => {
          if (gameState.batakVariant === "kozmaca") {
            // Calculate a rough bid based on hand strength: Spades over 10 + Aces
            const spadesCount = currentPlayer.hand.filter(
              (c) =>
                c.suit === "spades" && ["J", "Q", "K", "A"].includes(c.rank),
            ).length;
            const otherAces = currentPlayer.hand.filter(
              (c) => c.suit !== "spades" && c.rank === "A",
            ).length;
            // minimum bid 1, max 13
            let estimatedTricks = spadesCount + otherAces;
            if (estimatedTricks < 1) estimatedTricks = 1;
            handleBidGame(estimatedTricks);
          } else {
            const calculatedBestBid = AIService.getBestBid(
              gameState,
              currentPlayer,
            );
            const cMax = gameState.currentMaxBid || 4;
            let newBid: number | "pass" = "pass";
            if (calculatedBestBid > cMax) {
              // Bid the minimum required to take the lead, but we could also go straight to our best bid
              newBid = cMax + 1;
            } else if (
              gameState.isTeamGame &&
              cMax < 8 &&
              calculatedBestBid >= 7
            ) {
              newBid = 8;
            }
            handleBidGame(newBid);
          }
        }, effectiveBidDelay);
        return () => clearTimeout(timer);
      }
    }

    if (gameState?.status === "discarding") {
      const currentPlayer = gameState.players[gameState.turnIndex];
      if (currentPlayer?.isBot && isAmIHost(gameState, myId)) {
        const timer = setTimeout(() => {
          if (currentPlayer.hand.length === 0) return;
          // Discard the lowest valued card, not spades if possible
          const sortedHand = [...currentPlayer.hand].sort((a, b) => {
            const getVal = (c: any) => {
              const r = c.rank;
              const map: any = { J: 11, Q: 12, K: 13, A: 14 };
              const val = map[r] || parseInt(r);
              return c.suit === "spades" ? val + 100 : val;
            };
            return getVal(a) - getVal(b);
          });
          handlePlayCard(sortedHand[0].id, currentPlayer.id, false);
        }, discardDelay);
        return () => clearTimeout(timer);
      }
    }

    if (gameState?.status === "selecting") {
      const currentPlayer = gameState.players[gameState.turnIndex];
      if (currentPlayer?.isBot && isAmIHost(gameState, myId)) {
        const isInitialDeal =
          gameState.players.every((p) => (p.hand?.length || 0) >= 12) &&
          (!gameState.trumpSuit && (!gameState.activePenalty || gameState.activePenalty === "none"));
        const effectiveSelectDelay = isInitialDeal ? selectDelay + 2100 : selectDelay;
        const timer = setTimeout(() => {
          const choice = AIService.getBestChoice(gameState, currentPlayer);
          const botBid =
            gameState.gameType === "batak"
              ? AIService.getBestBid(gameState, currentPlayer)
              : undefined;
          handleSelectGame(choice, botBid);
        }, effectiveSelectDelay);
        return () => clearTimeout(timer);
      }
    }
  }, [
    gameSpeed,
    gameState?.turnIndex,
    gameState?.status,
    gameState?.currentBidderIndex,
    gameState?.currentTrick.length,
    gameState?.status === "discarding"
      ? gameState?.players[gameState.turnIndex]?.hand.length
      : null,
    gameState?.gameType === "okey"
      ? gameState?.players[gameState.turnIndex || 0]?.okeyState?.rack.length
      : null,
    gameState?.players.find((p) => !p.isBot)?.id === myId
      ? gameState?.players[gameState.turnIndex || 0]?.isBot
      : false,
    gameState?.players.find((p) => !p.isBot)?.id === myId
      ? gameState?.players[gameState.currentBidderIndex || 0]?.isBot
      : false,
    gameState?.lastAction?.timestamp,
    gameState?.middlePile?.length,
  ]);

  // Host-Authoritative AFK & Background Tab Safeguard for Multiplayer P2P
  useEffect(() => {
    if (!gameState || gameState.networkMode !== "P2P" || !isAmIHost(gameState, myId)) {
      return;
    }

    if (gameState.status === "playing" || gameState.status === "discarding") {
      const currentTurnPlayer = gameState.players[gameState.turnIndex];
      // Only watch remote human peers who have stalled
      if (currentTurnPlayer && !currentTurnPlayer.isBot && !isSamePlayerId(currentTurnPlayer.id, myId)) {
        const timeoutMs = gameState.gameType === "okey" ? 35000 : 22000;
        const currentTurnIdx = gameState.turnIndex;
        const currentStatus = gameState.status;

        const afkTimer = setTimeout(() => {
          const fresh = gameStateRef.current;
          if (fresh && fresh.turnIndex === currentTurnIdx && fresh.status === currentStatus) {
            console.warn(`[AFK Protection] Remote player ${currentTurnPlayer.name} is stalled/AFK. Auto-playing for continuity.`);
            if (fresh.gameType === "okey") {
              const rackLen = currentTurnPlayer.okeyState?.rack.length || 0;
              if (rackLen <= 14) {
                handleOkeyDraw(currentTurnPlayer.id, "deck");
              } else {
                const discardId = AIService.getBestOkeyDiscard(fresh, currentTurnPlayer);
                if (discardId) {
                  handleOkeyDiscard(currentTurnPlayer.id, discardId);
                }
              }
            } else if (currentTurnPlayer.hand && currentTurnPlayer.hand.length > 0) {
              const bestCardId = AIService.getBestMove(fresh, currentTurnPlayer);
              handlePlayCard(bestCardId, currentTurnPlayer.id, false);
            }
          }
        }, timeoutMs);

        return () => clearTimeout(afkTimer);
      }
    } else if (gameState.status === "bidding") {
      const bidder = gameState.players[gameState.currentBidderIndex || 0];
      if (bidder && !bidder.isBot && !isSamePlayerId(bidder.id, myId)) {
        const currentBidIdx = gameState.currentBidderIndex;
        const afkTimer = setTimeout(() => {
          const fresh = gameStateRef.current;
          if (fresh && fresh.status === "bidding" && fresh.currentBidderIndex === currentBidIdx) {
            console.warn(`[AFK Protection] Remote bidder ${bidder.name} is stalled. Auto-passing.`);
            handleBidGame("pass");
          }
        }, 22000);
        return () => clearTimeout(afkTimer);
      }
    }
  }, [
    gameState?.status,
    gameState?.turnIndex,
    gameState?.currentBidderIndex,
    gameState?.networkMode,
    myId,
  ]);

  const handleFaceStyleSelect = (
    style:
      | "classic"
      | "relief"
      | "elegant"
      | "minimalist"
      | "royal_gold"
      | "dark_knight"
      | "vintage"
      | "neon"
      | "casino"
      | "holographic"
      | "wooden"
      | "ocean"
      | "obsidian"
      | "pixel"
      | "cyberpunk"
      | "glassmorphism",
  ) => {
    setFaceStyle(style);
    if (!gameState) return;
    const newState = { ...gameState, faceStyle: style };
    updateGameState(newState);
  };

  const handleExitGame = (returnToLobby = true) => {
    try {
      networkRef.current?.broadcast("PLAYER_LEAVING", { id: myId });
    } catch (e) {}
    networkRef.current?.disconnect();
    networkRef.current = null;
    const audios = document.querySelectorAll(`audio[id^="audio-"]`);
    audios.forEach((el) => el.parentNode?.removeChild(el));
    setupNetwork(true);
    setGameState(null);
    setInGame(false);
    setIsVoiceActive(false);
    setIsMicMuted(true);
    if (returnToLobby) {
      setShowLobbyOnLanding(true);
      setLobbyInitialStep('MODE');
    } else {
      setShowLobbyOnLanding(false);
    }
  };

  const handleLogout = () => {
    networkRef.current?.disconnect();
    networkRef.current = null;
    const audios = document.querySelectorAll(`audio[id^="audio-"]`);
    audios.forEach((el) => el.parentNode?.removeChild(el));
    setupNetwork(true);
    try {
      localStorage.removeItem("userName");
      localStorage.removeItem("userAvatar");
      localStorage.removeItem("playerStats");
    } catch {}
    setUserName("Oyuncu 1");
    setUserAvatar(AVATAR_MAP['ahmet']);
    setStats({ gamesPlayed: 0, gamesWon: 0, totalScore: 0 });
    setGameState(null);
    setInGame(false);
    setIsVoiceActive(false);
    setIsMicMuted(true);
    setShowLobbyOnLanding(false);
  };

  const getSEOMeta = () => {
    switch (gameType) {
      case 'batak': return { title: 'Batak Online - Ücretsiz İhaleli & Eşli Batak Oyna | pisti.online', desc: 'Gerçek oyuncularla veya yapay zeka ile ücretsiz Batak oyna. Hemen masaya otur, ihaleli ve eşli batak keyfini yaşa.' };
      case 'king': return { title: 'King Online - Rıfkı ve Kız Almaz Oyna | pisti.online', desc: 'Arkadaşlarınla online King oyna. Rıfkı, Kız Almaz ve diğer cezalarla en iyi stratejini konuştur.' };
      case 'okey': return { title: 'Okey Online - 101 ve Klasik Okey Oyna | pisti.online', desc: 'Ücretsiz 101 ve Klasik Okey oyna, sohbet et ve eğlen. Gerçek oyuncularla online masa oyunları deneyimi.' };
      case 'pisti':
      default: return { title: 'Pişti Online - Gerçek Oyuncularla Pişti, King, Batak & Okey Oyna | pisti.online', desc: 'Gerçek oyuncularla Pişti, Okey, Batak ve King oyna. Türkiye\'nin modern kart ve masa oyunları platformunda ücretsiz canlı oyun keyfini yaşa.' };
    }
  };
  const seo = getSEOMeta();
  const currentPath = location.pathname.toLowerCase();
  const canonicalUrl = `https://pisti.online${currentPath === '/' ? '/' : currentPath}`;

  if (inGame && gameState && !preloadComplete) {
    return (
      <>
        <Helmet>
          <title>{seo.title}</title>
          <meta name="description" content={seo.desc} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:title" content={seo.title} />
          <meta property="og:description" content={seo.desc} />
          <meta property="og:url" content={canonicalUrl} />
        </Helmet>
        <PageLoader text={preloadText} progress={preloadProgress} />
      </>
    );
  }

  if (!inGame || !gameState) {
    return (
      <>
        <Helmet>
          <title>{seo.title}</title>
          <meta name="description" content={seo.desc} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:title" content={seo.title} />
          <meta property="og:description" content={seo.desc} />
          <meta property="og:url" content={canonicalUrl} />
        </Helmet>
        <Suspense fallback={<PageLoader text="Yükleniyor..." />}>
          <LandingPage
            myId={myId}
            shortId={shortId}
            onHost={handleHost}
            onJoin={handleJoin}
            playersCount={gameState?.players.length || 0}
            onGameTypeChange={handleGameTypeChange}
            gameType={gameType}
            selectedSkinId={selectedSkinId}
            onSkinSelect={setSelectedSkinId}
            faceStyle={faceStyle}
            onFaceStyleSelect={handleFaceStyleSelect}
            userAvatar={userAvatar}
            onAvatarChange={setUserAvatar}
            colorTheme={colorTheme}
            onColorThemeSelect={setColorTheme}
            userName={userName}
            onNameChange={setUserName}
            stats={stats}
            onLogout={handleLogout}
            onOpenCookiePolicy={() => setShowCookiePolicy(true)}
            onOpenTermsOfUse={() => setShowTermsOfUse(true)}
            isConnecting={isConnecting}
            initialShowLobby={showLobbyOnLanding}
            initialLobbyStep={lobbyInitialStep}
            initialRoomCode={roomCodeFromUrl || undefined}
            onLobbyClose={() => setShowLobbyOnLanding(false)}
          />
          {showCookiePolicy && (
            <CookiePolicy onClose={() => setShowCookiePolicy(false)} />
          )}
          {showTermsOfUse && (
            <TermsOfUse onClose={() => setShowTermsOfUse(false)} />
          )}
        </Suspense>
        <CookieBanner onOpenPolicy={() => setShowCookiePolicy(true)} isPolicyOpen={showCookiePolicy} />
        <ReloadPrompt inGame={inGame} />
        {!preloadComplete && (
          <div className="fixed bottom-4 left-4 md:left-auto md:right-4 z-[9999] bg-[#020617]/95 border border-cyan-500/30 rounded-xl p-3 shadow-2xl flex items-center gap-3 animate-pulse animate-duration-1000">
            <div className="relative flex h-3.5 w-3.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-cyan-500"></span>
            </div>
            <div className="flex flex-col text-left">
              <span className="text-[11px] font-bold text-white tracking-wide">
                {localStorage.getItem("language") === "tr" || navigator.language.startsWith("tr") ? "Oyun Kaynakları Yükleniyor" : "Loading Game Assets"}
              </span>
              <span className="text-[10px] text-cyan-400 font-semibold">{Math.round(preloadProgress)}% • {preloadText}</span>
            </div>
          </div>
        )}
        {/* Connection in progress modal */}
        {isConnecting && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-[#0b132b] border border-cyan-500/40 rounded-2xl p-6 max-w-sm w-full text-center shadow-[0_0_50px_rgba(6,182,212,0.25)] space-y-4">
              <div className="relative mx-auto w-14 h-14 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full border-4 border-cyan-500/20 border-t-cyan-400 animate-spin" />
                <Globe className="w-6 h-6 text-cyan-400 animate-pulse" />
              </div>
              <div className="space-y-1">
                <h3 className="text-white font-bold text-base tracking-wide">
                  {localStorage.getItem("language") === "tr" || navigator.language.startsWith("tr")
                    ? "Masaya Bağlanılıyor..."
                    : "Connecting to Table..."}
                </h3>
                <p className="text-xs text-white/60">
                  {localStorage.getItem("language") === "tr" || navigator.language.startsWith("tr")
                    ? "Oda aranıyor ve WebRTC bağlantısı kuruluyor..."
                    : "Searching for room and establishing WebRTC connection..."}
                </p>
              </div>
              <button
                onClick={() => {
                  setIsConnecting(false);
                  networkRef.current?.disconnect();
                }}
                className="w-full py-2.5 px-4 rounded-xl bg-white/10 hover:bg-white/15 text-white/80 text-xs font-semibold tracking-wider uppercase transition-colors"
              >
                {localStorage.getItem("language") === "tr" || navigator.language.startsWith("tr") ? "İptal" : "Cancel"}
              </button>
            </div>
          </div>
        )}
        {/* Connection error modal */}
        {connectionError && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-[#0b132b] border border-rose-500/40 rounded-2xl p-6 max-w-sm w-full text-center shadow-[0_0_50px_rgba(244,63,94,0.25)] space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-rose-500/10 border border-rose-500/30 flex items-center justify-center text-rose-400">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <h3 className="text-white font-bold text-base tracking-wide">
                  {localStorage.getItem("language") === "tr" || navigator.language.startsWith("tr")
                    ? "Bağlantı Kurulamadı"
                    : "Connection Failed"}
                </h3>
                <p className="text-xs text-white/70 leading-relaxed">
                  {connectionError}
                </p>
              </div>
              <button
                onClick={() => setConnectionError(null)}
                className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-400 hover:to-red-500 text-white text-xs font-bold tracking-wider uppercase transition-all shadow-md"
              >
                {localStorage.getItem("language") === "tr" || navigator.language.startsWith("tr") ? "Tamam" : "Dismiss"}
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  const handleToggleMic = async () => {
    safeRequestFullscreen();
    await toggleMute(networkRef);
    setTimeout(() => {
      safeRequestFullscreen();
    }, 150);
  };

  const myResolvedPlayer =
    gameState?.players.find((p) => p.id === myId) ||
    gameState?.players.find((p) => !p.isBot && isSamePlayerId(p.id, myId)) ||
    (userName
      ? gameState?.players.find(
          (p) => !p.isBot && p.name && p.name.trim().toLowerCase() === userName.trim().toLowerCase(),
        )
      : undefined);
  const effectiveMyId = myResolvedPlayer?.id || myId;

  return (
    <div className="w-full h-[100dvh] bg-[#0A0C10] relative">
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.desc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.desc} />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>
      <Suspense fallback={<PageLoader text="Oyun Masası Hazırlanıyor..." />}>
        <EmojiBurstOverlay />
        <ErrorBoundary fallbackTitle="Oyun Masasında Beklenmedik Bir Durum Oluştu">
        {gameState?.gameType === "okey" ? (
        <OkeyTable
          isMicMuted={isMicMuted}
          onToggleMic={handleToggleMic}
          myId={effectiveMyId}
          players={gameState.players}
          onExit={(toLobby?: boolean) => handleExitGame(toLobby ?? true)}
          gameState={gameState}
          shortId={shortId}
          onStartGame={handleStartGame}
          onAddBot={handleAddBot}
          onRemovePlayer={handleRemovePlayer}
          onToggleTeam={handleToggleTeam}
          onDealingComplete={handleDealingComplete}
          onUpdatePlayer={(updates) => {
            handleUpdatePlayer(effectiveMyId, updates);
            networkRef.current?.broadcast("UPDATE_PLAYER", updates);
          }}
          onTileDiscard={(tileId) => {
            const turnPlayer = gameState?.players[gameState.turnIndex];
            if (turnPlayer && isSamePlayerId(turnPlayer.id, effectiveMyId)) {
              handleOkeyDiscard(turnPlayer.id, tileId);
              networkRef.current?.broadcast("OKEY_DISCARD_ACTION", { tileId });
            }
          }}
          onTileDraw={(source) => {
            const turnPlayer = gameState?.players[gameState.turnIndex];
            if (turnPlayer && isSamePlayerId(turnPlayer.id, effectiveMyId)) {
              const isHost = isAmIHost(gameState, effectiveMyId);
              const hasMultipleHumans = (gameState?.players.filter((p) => !p.isBot).length || 0) > 1;
              if (isHost || !hasMultipleHumans || source === "discard") {
                handleOkeyDraw(turnPlayer.id, source);
              } else {
                soundManager.play("tile_draw");
              }
              networkRef.current?.broadcast("OKEY_DRAW_ACTION", { source });
            }
          }}
          onToggleDouble={() => {
            handleOkeyToggleDouble(effectiveMyId);
            networkRef.current?.broadcast("OKEY_TOGGLE_DOUBLE", {});
          }}
          onShowIndicator={() => {
            handleOkeyShowIndicator(effectiveMyId);
            networkRef.current?.broadcast("OKEY_SHOW_INDICATOR", {});
          }}
          onChatMessage={
            gameState.networkMode !== "AI" && (gameState.players.filter((p) => !p.isBot).length > 1 || (gameState.status === "waiting" && Boolean(gameState.roomId || shortId)))
              ? (text) => {
                  handleChatMessage(effectiveMyId, text);
                  networkRef.current?.broadcast("CHAT_MESSAGE", { text });
                }
              : undefined
          }
          chatMessages={chatMessages}
          isHost={
            isAmIHost(gameState, effectiveMyId)
          }
        />
      ) : (
        <Table
          isMicMuted={isMicMuted}
          onToggleMic={handleToggleMic}
          gameState={gameState!}
          myId={effectiveMyId}
          userName={userName}
          shortId={shortId}
          isDisconnected={isDisconnected}
          onPlayCard={(cardId, isBluff) => {
            const activeState = gameStateRef.current || gameState;
            if (!activeState || activeState.status === "dealing") return;
            const currentTurnPlayer = activeState.players[activeState.turnIndex];
            const isMyTurn = currentTurnPlayer && (
              isSamePlayerId(currentTurnPlayer.id, myId) ||
              (!currentTurnPlayer.isBot && userName && currentTurnPlayer.name && currentTurnPlayer.name.trim().toLowerCase() === userName.trim().toLowerCase()) ||
              (!currentTurnPlayer.isBot && activeState.players.filter((p) => !p.isBot).length === 1)
            );
            
            if (isMyTurn) {
              const activePlayerId = currentTurnPlayer.id;
              const isHost = isAmIHost(activeState, myId);
              handlePlayCard(cardId, activePlayerId, isBluff);

              if (!isHost) {
                const actionPayload = {
                  cardId,
                  isBluff,
                  stateHash: `${activeState.seed || ""}_${activeState.round || 0}_${activeState.turnIndex}_${cardId}`,
                  playerId: activePlayerId,
                  playerName: currentTurnPlayer.name,
                };
                networkRef.current?.broadcast("PLAYER_ACTION", actionPayload);

                // Auto-retry once after 700ms if host hasn't synced advanced turn
                const turnBefore = activeState.turnIndex;
                setTimeout(() => {
                  const checkState = gameStateRef.current;
                  if (checkState && checkState.turnIndex === turnBefore && checkState.status === "playing") {
                    console.log("[Multiplayer] Re-sending PLAYER_ACTION to host for zero-drop guarantee...");
                    networkRef.current?.broadcast("PLAYER_ACTION", actionPayload);
                  }
                }, 700);
              }
            }
          }}
          onStartGame={handleStartGame}
          onRestartGame={handleRestartGame}
          onBidGame={(bid) => {
            handleBidGame(bid);
            networkRef.current?.broadcast("BID_ACTION", { bid });
          }}
          onSelectGame={(type, bid) => {
            handleSelectGame(type, bid);
            networkRef.current?.broadcast("SELECT_GAME", { type, bid });
          }}
          onExitGame={(toLobby?: boolean) => handleExitGame(toLobby ?? true)}
          onUpdatePlayer={(updates) => {
            handleUpdatePlayer(myId, updates);
            networkRef.current?.broadcast("UPDATE_PLAYER", updates);
          }}
          onAddBot={handleAddBot}
          onRemovePlayer={handleRemovePlayer}
          onToggleTeam={handleToggleTeam}
          onDealingComplete={handleDealingComplete}
          onResolveBluff={(isChallenge) => {
            handleResolveBluff(myId, isChallenge);
            networkRef.current?.broadcast("RESOLVE_BLUFF", { isChallenge });
          }}
          onChatMessage={
            gameState.networkMode !== "AI" && (gameState.players.filter((p) => !p.isBot).length > 1 || (gameState.status === "waiting" && Boolean(gameState.roomId || shortId)))
              ? (text) => {
                  handleChatMessage(myId, text);
                  networkRef.current?.broadcast("CHAT_MESSAGE", { text });
                }
              : undefined
          }
          chatMessages={chatMessages}
          selectedSkinId={selectedSkinId}
          onSkinSelect={setSelectedSkinId}
          selectedTheme={selectedTheme}
          onThemeSelect={setSelectedTheme}
          faceStyle={faceStyle}
          onFaceStyleSelect={handleFaceStyleSelect}
          colorTheme={colorTheme}
          onColorThemeSelect={setColorTheme}
        />
      )}
        </ErrorBoundary>
      </Suspense>
      {/* PWA / Network Online & Offline Status Banner */}
      {showNetworkNotice && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[99999] pointer-events-auto transition-all animate-fade-in">
          <div
            className={`flex items-center gap-3 px-4 py-2.5 rounded-full text-xs font-medium shadow-2xl border backdrop-blur-md ${
              isOnline
                ? "bg-emerald-950/95 border-emerald-500/50 text-emerald-200 shadow-emerald-900/40"
                : "bg-amber-950/95 border-amber-500/50 text-amber-200 shadow-amber-900/40"
            }`}
          >
            <span className="relative flex h-2.5 w-2.5">
              <span
                className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  isOnline ? "bg-emerald-400" : "bg-amber-400"
                }`}
              ></span>
              <span
                className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                  isOnline ? "bg-emerald-500" : "bg-amber-500"
                }`}
              ></span>
            </span>
            <span>
              {isOnline
                ? (localStorage.getItem("language") === "tr" || navigator.language.startsWith("tr")
                    ? "İnternet bağlantısı yeniden sağlandı."
                    : "Internet connection restored.")
                : (localStorage.getItem("language") === "tr" || navigator.language.startsWith("tr")
                    ? "Çevrimdışı mod: İnternet kesildi, botlarla oynamaya devam edebilirsiniz."
                    : "Offline mode: Connection lost, you can continue playing with bots.")}
            </span>
            <button
              onClick={() => setShowNetworkNotice(false)}
              className="ml-2 text-slate-400 hover:text-white transition-opacity font-bold px-1"
            >
              ✕
            </button>
          </div>
        </div>
      )}
      <CookieBanner onOpenPolicy={() => setShowCookiePolicy(true)} isPolicyOpen={showCookiePolicy} />
      {showCookiePolicy && (
        <CookiePolicy onClose={() => setShowCookiePolicy(false)} />
      )}
      {showTermsOfUse && (
        <TermsOfUse onClose={() => setShowTermsOfUse(false)} />
      )}
      <ReloadPrompt inGame={inGame} />
    </div>
  );
}
